import { useCallback, useEffect, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  MapPin,
  Maximize2,
  Pencil,
  ClipboardList,
  Trash2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { compressImage } from "@/lib/compress-image";
import { ensureRenderable } from "@/lib/heic/ensure-renderable";
import { personDisplayName } from "@/lib/people";
import { useSwipeNavigation } from "@/hooks/use-swipe-navigation";
import { TagSelector } from "@/components/records/tag-selector/tag-selector";
import { DeleteRecordDialog } from "@/components/records/delete-record-dialog/delete-record-dialog";
import { RemovePhotoDialog } from "@/components/records/remove-photo-dialog/remove-photo-dialog";
import { ExpandedPlanView } from "@/components/records/expanded-plan-view/expanded-plan-view";
import { PinMarkerDot } from "@/components/common/pin-marker-dot/pin-marker-dot";
import { PinDirectionCone } from "@/components/common/pin-direction-cone/pin-direction-cone";
import { PinRotateHandle } from "@/components/common/pin-rotate-handle/pin-rotate-handle";
import {
  PinDirectionHint,
  PIN_DIRECTION_HINT_TEXT,
} from "@/components/common/pin-direction-hint/pin-direction-hint";
import { useDragToRotate } from "@/hooks/use-drag-to-rotate";
import { useProjectPreviousTags } from "@/hooks/use-previous-tags";
import {
  OfflineImage,
  useOfflineImageUrl,
} from "@/components/common/offline-image/offline-image";
import {
  planAssetKey,
  thumbAssetKey,
  thumbAssetKeyFor,
} from "@/offline/asset-keys";
import { RecordPhotoGrid } from "@/components/records/record-photo-grid/record-photo-grid";
import type { RecordPhotoTile } from "@/components/records/record-photo-grid/record-photo-grid";
import {
  RecordPhotoViewer,
  type RecordPhotoViewerItem,
} from "@/components/records/record-photo-viewer/record-photo-viewer";
import { coverImage, recordImages, recordPhotoCount } from "@/lib/record-images";
import {
  formatCapturedAt,
  recordLatestPhotoAt,
} from "@/lib/records/captured-at";
import type { CapturedPhoto } from "@/components/common/record-capture/captured-photo";
import { ImageMarkupEditor } from "@/components/records/image-markup-editor";
import type { MarkupCompletePayload } from "@/components/records/image-markup-editor";
import type { Drawing } from "@/components/records/image-markup-editor/types";
import type { CropRect } from "@/components/records/image-markup-editor/crop-overlay";
import {
  toRotation,
  type Rotation,
} from "@/components/records/image-markup-editor/rotation";
import { useAuth } from "@workos-inc/authkit-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { ApiError, apiFetch } from "@/lib/api";
import { useOnlineStatus } from "@/offline/online-status";
import {
  appendPhotosToPendingRecord,
  findPendingRecord,
  putPendingAppend,
  removePhotoFromPendingRecord,
  updatePendingAppendStatus,
  updatePendingRecordPhoto,
  type PendingIssue,
} from "@/offline/db";
import { normalizeRecordImageShape } from "@/offline/record-shape";
import { usePendingRecords } from "@/hooks/use-pending-records";
import { usePendingAppends } from "@/hooks/use-pending-appends";
import { useRecordCapture } from "@/components/common/record-capture/use-record-capture";
import type { ProjectRecord } from "@/types/api";
import { RecordShareMenu } from "@/components/records/record-share-menu/record-share-menu";
import { useAutodeskConnection } from "@/hooks/use-autodesk";
import { issueTitleOrPlaceholder } from "@/lib/issues";
import { RaiseIssueSheet } from "@/components/issues/raise-issue-sheet/raise-issue-sheet";
import { RecordIssuesBand } from "@/components/issues/record-issues-band/record-issues-band";
import {
  RaiseIssueToggle,
  type RaiseIssueDraft,
} from "@/components/issues/raise-issue-toggle/raise-issue-toggle";
import { renderField } from "./field-renderers";
import { readMetadataByFieldName } from "./use-submit-record";
import { useMetadataForm } from "./use-metadata-form";
import { usePendingRecordPhotos } from "./use-pending-record-photos";
import { capturedToPendingPhoto, useSubmitRecord } from "./use-submit-record";
import { useDeleteRecordImage } from "./use-delete-record-image";
import {
  useUpdateRecordImage,
  type UpdateRecordImageVars,
} from "./use-update-record-image";
import { useVoiceCapture } from "./use-voice-capture";
import { VoiceCaptureButton } from "./voice-capture-button";
import {
  aiRemarksRangeOf,
  appendVoiceRemarks,
  mergeVoiceTags,
} from "./voice-apply";
import { clientToImageCoords } from "./utils";
import type { ImageViewerModalProps } from "./types";

/**
 * Thumbnail direction affordance — full-size, matching the fullscreen editor
 * (see [[directional-pins]]). The thumbnail has no zoom, so `--pin-scale` is
 * unset (= 1) and the cone (radius 56) + ~44px knob render at native size; a
 * pin near the thumbnail's edge therefore has its handle clipped by the
 * container's `overflow-hidden` — the accepted trade-off for consistency.
 */
const THUMBNAIL_CONE_RADIUS_PX = 56;
const THUMBNAIL_HANDLE_REST_BEARING = 0;
const THUMBNAIL_TAP_THRESHOLD_PX = 5;

export function ImageViewerModal({
  isOpen,
  onClose,
  imageSrc,
  imageFile,
  annotatedBlob,
  drawings = [],
  crop,
  capturedPhotos,
  takenAtHint,
  projectId,
  metadataFields,
  projectTags = [],
  projectPlans = [],
  initialRecord,
  viewOnly = false,
  defaultPlanId,
  defaultPinX,
  defaultPinY,
  defaultPinBearing,
  onNavigatePrev,
  onNavigateNext,
  currentIndex,
  totalCount,
  isFetchingNextPage,
  canOnResource,
  canCreate,
  canCreateTag = false,
  canCreateIssue = false,
  showRecordIssues = false,
  onEditMarkups,
  onAddPhotos,
  onRemovePhoto,
  presentation = "dialog",
  onUnsavedChangesChange,
}: ImageViewerModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [localTags, setLocalTags] = useState(projectTags);
  useEffect(() => {
    setLocalTags(projectTags);
  }, [projectTags]);

  const availablePlans = projectPlans.filter((p) => !p.deleted_at);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // The synced multi-photo per-photo delete is confirmed before it fires (a
  // stray tap on the small per-tile "×" is permanent + project-wide). Holds the
  // tapped photo's identity while the [[remove-photo-dialog]] confirm is open;
  // null when no confirm is pending. The lone-photo "×", queued-draft splice,
  // and new-batch removal never set this — they keep their existing routing.
  const [photoPendingRemoval, setPhotoPendingRemoval] = useState<{
    recordId: string;
    imageId: string;
  } | null>(null);
  const [isExpandedPlan, setIsExpandedPlan] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const sharePromiseRef = useRef<Promise<Blob | null> | null>(null);
  const shareAbortRef = useRef<AbortController | null>(null);
  const shareErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    setCanShare(typeof navigator !== "undefined" && !!navigator.share);
  }, []);

  // A "new" capture can arrive either as a multi-photo batch (`capturedPhotos`,
  // length >= 1) or the legacy single `imageFile`/`annotatedBlob`. Either marks
  // this as a not-yet-existing record.
  const hasCapturedBatch = !!capturedPhotos && capturedPhotos.length > 0;
  const isExistingRecord = !!initialRecord && !imageFile && !hasCapturedBatch;

  const [isRaiseIssueOpen, setIsRaiseIssueOpen] = useState(false);
  // Opt-in, from the caller. Deriving it here with `useProject` was tried and
  // reverted: it fired a project fetch this modal had never made, which the
  // public share page answers with a 401, and it crashed on the share page's
  // project shape. Callers that want the affordance pass the flag; everyone
  // else is unaffected.
  //
  // Only against a record that exists on the server — an issue needs a record
  // id to point at, and a draft mid-capture has none yet.
  const canRaiseIssue =
    !!canCreateIssue && !viewOnly && isExistingRecord && !!initialRecord;
  // Remarks is a project metadata FIELD, not a column, so it is found by name
  // — but read by id, which is how it is actually stored. See
  // readMetadataByFieldName for why that distinction bites.
  // The form's current Remarks value — for a NEW record there is no
  // initialRecord to read, and the user has usually just typed them.
  const recordRemarksFromForm = (data: Record<string, unknown>) => {
    const field = metadataFields.find(
      (f) => f.name.toLowerCase() === "remarks",
    );
    return field ? data[field.id] : undefined;
  };

  /**
   * The issue block to queue with a new record, or null when the record was
   * not promoted (no assignee, no due date). Shared by both create paths (single- and multi-photo) so the two
   * cannot drift.
   *
   * The title falls back to the remarks the user just typed, then to a
   * placeholder. The card never blocks a submit on an empty title — someone
   * rushing a walk is not stopped by a form — so the fallback chain is what
   * guarantees the required field is non-blank. See
   * [[raise-issue-toggle.spec]].
   */
  const buildPendingIssue = (
    data: Record<string, unknown>,
  ): PendingIssue | null =>
    issueDraft
      ? {
          clientUuid: crypto.randomUUID(),
          title: issueTitleOrPlaceholder(
            issueDraft.title,
            String(recordRemarksFromForm(data) ?? ""),
          ),
          description: "",
          assignedToId: issueDraft.assignedToId,
          dueDate: issueDraft.dueDate || null,
        }
      : null;

  const recordRemarks = readMetadataByFieldName(
    metadataFields,
    initialRecord?.metadata,
    "remarks",
  );
  // Set for the issue just raised so the band can tint its row, cleared on a
  // timer. The toast is the loud half of the confirmation; this is the half
  // that survives being missed.
  const [justRaisedIssueId, setJustRaisedIssueId] = useState<string | null>(
    null,
  );
  // New records only. Non-null means the capture also raises an issue, on the
  // same request. The "Promote as issue" card sets it as soon as an assignee or
  // due date is picked — see [[raise-issue-toggle.spec]].
  const [issueDraft, setIssueDraft] = useState<RaiseIssueDraft | null>(null);
  const [isEditMode, setIsEditMode] = useState(!isExistingRecord);

  // The modal's presentation mode, derived from props/state (no mode prop, no
  // feature flag — see [[image-viewer-modal]]). `new` for a fresh capture,
  // else `edit` once the user taps Edit, else `view`. `viewOnly` (share page)
  // stays in view chrome.
  const mode: "view" | "edit" | "new" = !isExistingRecord
    ? "new"
    : isEditMode && !viewOnly
      ? "edit"
      : "view";

  // "Use previous tags" — the tag set carried over from the last record THIS
  // user created in THIS project. New records only; the memory is per-user by
  // construction, so a shared site device never leaks one worker's tags to the
  // next. See [[image-viewer-modal.spec]] ("Previous tags on create").
  const previousTagsMemory = useProjectPreviousTags(projectId, localTags);
  // Frozen at mount: this is what seeds the form's default `__tags`, and the
  // form's defaults are only ever read on the first render of a new record.
  // Holding it still also gives the off-switch an exact list to undo.
  const [seededPreviousTags] = useState<string[]>(() =>
    isExistingRecord || !previousTagsMemory.enabled
      ? []
      : previousTagsMemory.tagIds,
  );
  const appliedPreviousTagsRef = useRef<string[]>(seededPreviousTags);

  // Fullscreen carousel ([[record-photo-viewer]]) — opened from a grid tile.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Graceful message for the two still-unwired multi-photo ops on a NEW draft:
  // adding a photo to, or removing one from, an un-saved batch (both owned by
  // the capture tray — see Out of Scope). Multi-photo CREATE no longer uses
  // this: it queues to the outbox like single-photo (`submitMultiRecord`).
  // Cleared on record swap / modal close alongside the other per-open state.
  const [multiPhotoError, setMultiPhotoError] = useState<string | null>(null);

  // Pending vs synced detection. A record is a local outbox draft iff an
  // outbox entry exists for its id — and a pending record's `id` IS its
  // `clientUuid` (see [[offline-record-outbox]]). This drives the coherent
  // offline-mutability rule: a record is editable/deletable offline iff it
  // is still pending.
  //
  // Derived REACTIVELY from the `["pendingRecords", projectId]` query (via
  // `usePendingRecords`), not a one-shot async `getPendingRecord` read: if
  // the background drain syncs this record while the modal is open, the
  // outbox entry is removed, the query refetches, and `isPendingRecord`
  // flips to `false` the same render. A stale snapshot would leave the
  // synced-offline gate wrong — e.g. a delete firing a raw server `DELETE`
  // and surfacing an `OfflineError` toast (see [[offline-record-outbox]] H3).
  const pendingTiles = usePendingRecords(projectId);
  const pendingTile =
    isExistingRecord && initialRecord?.id
      ? pendingTiles.find((tile) => tile.clientUuid === initialRecord.id)
      : undefined;
  const isPendingRecord = !!pendingTile;

  // Latch: did this modal open on an outbox draft? A draft's `id` is its
  // `clientUuid`, which the server never adopts as the record's pk — so once
  // this modal has seen the record as a pending draft, that id must never be
  // sent in a server PATCH/DELETE, even after the upload lands and
  // `isPendingRecord` flips false. Derived DURING render (not via an effect) so
  // the latch is set the same render `isPendingRecord` first turns true — no
  // one-frame window where a just-synced draft briefly looks editable (which,
  // online, would re-open the 404). React's "adjust state while rendering"
  // pattern; the guards make it self-terminating, and it resets when the modal
  // switches to a different record. See [[offline-record-outbox]] — "a draft
  // that synced while its viewer was open".
  const [openedAsDraft, setOpenedAsDraft] = useState(false);
  const [latchedRecordId, setLatchedRecordId] = useState(initialRecord?.id);
  if (initialRecord?.id !== latchedRecordId) {
    setLatchedRecordId(initialRecord?.id);
    setOpenedAsDraft(false);
  } else if (isPendingRecord && !openedAsDraft) {
    setOpenedAsDraft(true);
  }

  // A pending record's image must be resolved REACTIVELY from the live
  // pending tile, never from the frozen `initialRecord` snapshot. Editing a
  // pending record bumps its `revision`; `usePendingRecords` then revokes the
  // old `clientUuid:revision-N` object URL and mints a fresh one for
  // revision-N+1. The `selectedRecord` snapshot records-view captured at
  // tile-tap time still carries the now-revoked revision-N URL, so rendering
  // it would leave a dead `blob:` `<img>` after every edit. `pendingTile`
  // tracks the query, so its `thumbnailUrl` is always the live URL. A pending
  // record has only its one local annotated image — there is no separate
  // high-res — so the same live URL is used everywhere the modal derives an
  // image source for a pending record. See [[offline-record-outbox]].
  const pendingTileUrl = pendingTile?.thumbnailUrl ?? null;

  // Per-photo previews of a QUEUED MULTI-photo draft. The cover renders via
  // `pendingTileUrl` above (the entry mirrors its blob at the top level), but a
  // multi draft's additional photos live only inside the outbox entry's
  // `photos[]` — no server `images[]`, no asset cache. This reads the entry on
  // open and mints an object URL per photo so the grid + carousel render the
  // whole group from local bytes, online or offline. Empty for a single-photo
  // draft or a synced record, so the existing single-cover path is untouched.
  // See [[offline-record-outbox]] (multi-photo subsection).
  const pendingDraftPhotos = usePendingRecordPhotos(
    initialRecord?.id,
    isPendingRecord,
  );
  const isMultiPhotoDraft = pendingDraftPhotos.length > 1;
  // Synchronous "this is a queued multi-photo draft" signal — known from the
  // synthesized placeholder's image count on the FIRST render, before the async
  // per-photo blob read (`pendingDraftPhotos`) resolves. `isMultiPhotoDraft`
  // alone is false on that first frame, which would fall through to the
  // placeholder's N images (whose non-cover entries all reuse the cover URL) and
  // flash N identical covers. This flag lets the grid/carousel render a
  // cover-only frame until the real blobs arrive. See code-review S1.
  const isPendingMultiDraftEntry =
    isPendingRecord && !!initialRecord && recordPhotoCount(initialRecord) > 1;

  // Optimistic append tiles ([[append-photo-outbox]]). A synced record's queued
  // photos render in the open viewer as ORDINARY tiles (no syncing badge),
  // straight from local bytes, after the server's `images`. Keyed by the real
  // record id; the empty string is a no-op read for a new draft (no record id),
  // mirroring how the other reactive reads degrade. Invalidated by this modal's
  // queue-and-close, the drain, and tap-to-retry via the `["pendingAppends"]`
  // prefix.
  const pendingAppendTiles = usePendingAppends(
    isExistingRecord && initialRecord ? initialRecord.id : "",
  );

  const isOnlineStatus = useOnlineStatus();
  // The single gate the edit and delete affordances follow. A record is mutable
  // in exactly two states: a *queued* draft (pending AND offline — nothing
  // uploading) or a *synced* record while online. Every other state is locked —
  // see [[offline-record-outbox]]:
  //  - a draft while online is *syncing*; its real id isn't placed yet, so a
  //    PATCH would hit the temporary `clientUuid` and 404,
  //  - a draft that finished syncing while open (`openedAsDraft` latched while
  //    `isPendingRecord` is now false) still holds that temporary id — stay
  //    locked; reopen the synced record to edit it,
  //  - a synced record while offline needs connectivity (conflict resolution is
  //    v2).
  const recordActionsBlocked =
    isExistingRecord &&
    (isPendingRecord ? isOnlineStatus : openedAsDraft ? true : !isOnlineStatus);
  // Inline explanation shown wherever an affordance is disabled: a draft that is
  // syncing (or just synced while open) reads as a sync state; a synced record
  // with no connectivity reads as offline.
  const recordActionsBlockedMessage = !recordActionsBlocked
    ? null
    : isPendingRecord
      ? "Syncing… available to edit once saved"
      : openedAsDraft
        ? "Saved — reopen to edit"
        : "Offline — reconnect to edit";
  // A synced record caches only its thumbnail offline (see asset-backfill).
  // Resolve that cached blob so the viewer can render the record with no
  // network. Pending records carry their own local blob via `pendingTileUrl`,
  // so keep the key empty for them — `useOfflineImageUrl("", "")` is a no-op.
  const recordThumbAssetKey =
    initialRecord && !isPendingRecord ? thumbAssetKey(initialRecord) : "";
  const cachedThumbUrl = useOfflineImageUrl(recordThumbAssetKey, "") || null;
  // Suppress the remote high-res fetch while offline — the thumbnail layer
  // below still renders, and there's no point queuing an `<img>` request that
  // will surface as a broken-image icon. Local blob URLs (re-edit previews,
  // fresh upload) are unaffected: they go through the re-edit preview
  // overrides / `effectiveSrc` directly.
  //
  // For a pending record there is no remote high-res — its only image is the
  // local annotated blob — so the live `pendingTileUrl` IS the high-res
  // source, online or offline. It is never the frozen snapshot URL.
  const highResSrc = isPendingRecord
    ? pendingTileUrl
    : isOnlineStatus
      ? ((initialRecord && coverImage(initialRecord)?.image) ?? imageSrc ?? null)
      : null;

  const planThumbnailRef = useRef<HTMLImageElement>(null);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState,
    reset: resetForm,
    setValue,
    getValues,
  } = useMetadataForm({
    isOpen,
    recordId: initialRecord?.id ?? null,
    metadataFields,
    initialValues: initialRecord?.metadata,
    recordDefaults: {
      tags: initialRecord?.tags ?? seededPreviousTags,
      planId:
        initialRecord?.plan ?? defaultPlanId ?? availablePlans[0]?.id ?? null,
      pinX: initialRecord?.pin_x ?? defaultPinX ?? null,
      pinY: initialRecord?.pin_y ?? defaultPinY ?? null,
      pinBearing: initialRecord?.pin_bearing ?? defaultPinBearing ?? null,
    },
  });

  const selectedTags = (useWatch({ control, name: "__tags" }) as
    | string[]
    | undefined) ?? [];
  const selectedPlanId =
    (useWatch({ control, name: "__plan" }) as string | null | undefined) ??
    null;
  const pinX =
    (useWatch({ control, name: "__pinX" }) as number | null | undefined) ??
    null;
  const pinY =
    (useWatch({ control, name: "__pinY" }) as number | null | undefined) ??
    null;
  const pinBearing =
    (useWatch({ control, name: "__pinBearing" }) as
      | number
      | null
      | undefined) ?? null;

  // Inline direction editing — the thumbnail is the mini of the fullscreen
  // editor. Editable only in edit mode (never view-only / share). The handle
  // writes the same `__pinBearing` the expanded editor does. The gesture's
  // pin-centre anchor is the dot's own DOM rect; it is enabled only with a pin
  // (a bearing requires a pin). See [[directional-pins]].
  const canSetThumbnailDirection = isEditMode && !viewOnly;
  const thumbnailPinDotRef = useRef<HTMLDivElement>(null);
  const thumbnailPointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const { handleProps: thumbnailRotateProps } = useDragToRotate({
    anchorRef: thumbnailPinDotRef,
    onChange: (b) => setValue("__pinBearing", b, { shouldDirty: true }),
    enabled: canSetThumbnailDirection && pinX !== null && pinY !== null,
  });

  // Voice slot-fill (US-012). The hook owns the recorder + upload; the
  // modal owns the apply-result effect (setValue per field, additive tag
  // merge, Remarks append, 2-second highlight ring). The mic button only
  // renders when the modal is in edit mode AND online — voice is
  // inherently online; offline a disabled mic invites confusion.
  const voiceMode: "create" | "edit" = isExistingRecord ? "edit" : "create";
  const {
    state: voiceState,
    result: voiceResult,
    error: voiceError,
    elapsedMs: voiceElapsedMs,
    start: voiceStart,
    stop: voiceStop,
    reset: voiceReset,
    isSupported: voiceSupported,
  } = useVoiceCapture({
    projectId,
    mode: voiceMode,
    recordId: voiceMode === "edit" ? (initialRecord?.id ?? null) : null,
  });

  // Non-tag, non-remarks metadata fields get a brief ring highlight when AI
  // fills them. Tags + Remarks have their own richer distinction (see below).
  const [highlightedFieldIds, setHighlightedFieldIds] = useState<Set<string>>(
    new Set(),
  );
  // Tag IDs newly selected by the latest voice call and not yet
  // acknowledged. Drives the AI-tinted pill state in TagSelector. Cleared
  // per-id when the user taps an AI pill (accept), and globally on modal
  // close / record swap.
  const [aiSelectedTagIds, setAiSelectedTagIds] = useState<string[]>([]);
  // Ordered list of tag ids that voice fill has promoted to the front of
  // the picker. Sticky — entries are NOT removed when the user accepts
  // (tap once) or later deselects an AI pill. Only voice-fill batches add
  // to this list; user toggles never modify it. Resets in
  // clearVoiceFillState (modal close / record swap).
  const [aiPromotedOrder, setAiPromotedOrder] = useState<string[]>([]);
  // The Remarks field id + character range of the most recent AI append.
  // Drives the inline typewriter on the Remarks textarea. While
  // streaming, the form value is grown word-by-word via setValue on each
  // interval tick — the visual signal IS the textarea filling itself in.
  // Stream completes when revealedWords === words.length (set null), on
  // Remarks focus (handleRemarksFocus flushes to the full value), or on
  // modal-close / record-swap (clearVoiceFillState).
  const [aiRemarksStream, setAiRemarksStream] = useState<{
    fieldId: string;
    /** Everything up to and including the "\n\n" separator before the
     * AI portion. The textarea always reads `prefix + revealed words`. */
    prefix: string;
    words: string[];
    revealedWords: number;
  } | null>(null);

  // Drive the typewriter — advance revealedWords by 1 every 55ms until
  // we hit the end. Keyed off the stream-start identity so the interval
  // is created once per stream, not once per word tick. Each tick calls
  // setValue to grow the actual textarea value.
  const aiRemarksStreamKey = aiRemarksStream
    ? `${aiRemarksStream.fieldId}:${aiRemarksStream.words.length}`
    : null;
  useEffect(() => {
    if (!aiRemarksStreamKey) return;
    const id = setInterval(() => {
      setAiRemarksStream((prev) => {
        if (!prev) return prev;
        if (prev.revealedWords >= prev.words.length) {
          // Stream done — clear so isVoiceBusy can settle.
          return null;
        }
        const nextRevealed = prev.revealedWords + 1;
        const partial =
          prev.prefix + prev.words.slice(0, nextRevealed).join("");
        setValue(prev.fieldId, partial, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        if (nextRevealed >= prev.words.length) return null;
        return { ...prev, revealedWords: nextRevealed };
      });
    }, 55);
    return () => clearInterval(id);
  }, [aiRemarksStreamKey, setValue]);
  // True while the cascade reveal is applying values (between voiceResult
  // arriving and the last timer firing ~1100ms later). Combined with
  // voiceState to gate destructive actions (Submit, Delete, Edit markups)
  // so the user can't accidentally fire them mid-fill.
  const [isCascadeRunning, setIsCascadeRunning] = useState(false);

  // Destructive / form-mutating actions (Submit, Delete, Edit markups) are
  // blocked while voice is recording, transcribing, or applying values via
  // the cascade. Without this, the user can accidentally submit a record
  // before the AI fill lands, race the cascade's setValue calls, or open
  // the markup editor mid-recording and lose the in-flight audio.
  const isVoiceBusy =
    voiceState === "recording" ||
    voiceState === "processing" ||
    isCascadeRunning ||
    // The inline Remarks typewriter is still growing the textarea value
    // — disable destructive actions so the user doesn't submit a half-
    // written value. Stream auto-clears on completion or on focus.
    aiRemarksStream !== null;

  // Refs for staged cascade timers so we can cancel them on unmount /
  // record swap / explicit reset.
  const cascadeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const cancelCascade = useCallback(() => {
    cascadeTimersRef.current.forEach((t) => clearTimeout(t));
    cascadeTimersRef.current = [];
  }, []);

  const clearVoiceFillState = useCallback(() => {
    cancelCascade();
    setHighlightedFieldIds(new Set());
    setAiSelectedTagIds([]);
    setAiPromotedOrder([]);
    setAiRemarksStream(null);
    setIsCascadeRunning(false);
  }, [cancelCascade]);

  // Cancel pending cascade timers if the component unmounts entirely
  // (e.g. parent route change). Modal close and record swap are handled
  // by the two effects below.
  useEffect(() => {
    return () => cancelCascade();
  }, [cancelCascade]);

  // Reset voice state when the modal closes. Stops any active recording,
  // releases the mic stream, clears AI-fill state.
  useEffect(() => {
    if (!isOpen) {
      voiceReset();
      clearVoiceFillState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Independently reset voice state when navigating between records WHILE
  // the modal stays open. The close-driven effect above is gated by
  // `!isOpen` and doesn't fire on a record swap. Without this, a recording
  // started on record A and still in flight when the user navigates to
  // record B would land its parsed result on record B's form — wrong
  // record entirely. The reset aborts the in-flight upload (abortedRef
  // path in `useVoiceCapture`) so a late `onstop` cannot apply.
  const firstRecordIdRef = useRef(true);
  useEffect(() => {
    if (firstRecordIdRef.current) {
      firstRecordIdRef.current = false;
      return;
    }
    voiceReset();
    clearVoiceFillState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecord?.id]);

  // Apply the voice result to the form as a cascade: non-remarks fields +
  // tags first; Remarks 900ms later. Lets the user see Tags travel into
  // the selected zone before the Remarks block lands.
  useEffect(() => {
    if (!voiceResult) return;

    // Cancel any pending cascade from a prior voice call before starting
    // a new one. This is the only place we need to cancel timers from
    // within the effect — the modal-close / record-swap paths clear via
    // clearVoiceFillState().
    cancelCascade();
    setIsCascadeRunning(true);

    const remarksField = metadataFields.find(
      (f) => f.name.toLowerCase() === "remarks",
    );

    // --- Stage 1 (t=0): non-remarks metadata fields + tag merge ---
    const stageOneHighlights: string[] = [];
    for (const [fieldId, rawValue] of Object.entries(
      voiceResult.fields ?? {},
    )) {
      if (rawValue == null) continue;
      if (remarksField && fieldId === remarksField.id) continue;
      const matchedField = metadataFields.find((f) => f.id === fieldId);
      if (!matchedField) continue;
      setValue(fieldId, rawValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      stageOneHighlights.push(fieldId);
    }

    let stagedAiTagIds: string[] = [];
    if (voiceResult.tagNames && voiceResult.tagNames.length > 0) {
      const currentTags =
        (getValues("__tags") as string[] | null | undefined) ?? [];
      const merge = mergeVoiceTags(
        currentTags,
        voiceResult.tagNames,
        localTags,
      );
      if (merge.mergedIds !== currentTags) {
        setValue("__tags", merge.mergedIds, { shouldDirty: true });
      }
      stagedAiTagIds = merge.aiSelectedTagIds;
    }

    if (stageOneHighlights.length > 0) {
      setHighlightedFieldIds(new Set(stageOneHighlights));
    }
    // Cancel any in-flight typewriter from a prior voice call. The
    // textarea will be overwritten by this call's stream anyway, but
    // clearing state up front means the old stream doesn't tick once
    // more before this one begins.
    setAiRemarksStream(null);
    // Promote this batch of ids to the front of the picker. Only voice
    // fill modifies the promoted order — user toggles never trigger a
    // re-sort. Newest batch lands at the very front; within the batch
    // we preserve the AI's returned order. Ids that were already
    // promoted by an earlier voice call stay in their existing slots
    // (we don't shuffle them).
    if (stagedAiTagIds.length > 0) {
      setAiPromotedOrder((prev) => {
        const known = new Set(prev);
        const additions = stagedAiTagIds.filter((id) => !known.has(id));
        if (additions.length === 0) return prev;
        return [...additions, ...prev];
      });
    }
    // Stagger tag tints so they appear one at a time (the FLIP reorder
    // in TagSelector rides on the aiPromotedOrder change above; this
    // loop is just for the tint reveal, not for the position change).
    setAiSelectedTagIds([]);
    stagedAiTagIds.forEach((id, i) => {
      const t = setTimeout(
        () => setAiSelectedTagIds((prev) => [...prev, id]),
        i * 280,
      );
      cascadeTimersRef.current.push(t);
    });

    // --- Stage 2 (t=900): Remarks ---
    if (remarksField) {
      const remarksRaw = voiceResult.fields?.[remarksField.id];
      if (remarksRaw != null) {
        const serverBlock = String(remarksRaw);
        const remarksTimer = setTimeout(() => {
          const existing = String(getValues(remarksField.id) ?? "");
          const appended = appendVoiceRemarks(existing, serverBlock);
          const range = aiRemarksRangeOf(existing, serverBlock);
          const prefix = appended.slice(0, range.start);
          const aiText = appended.slice(range.start, range.end);
          const words = aiText.split(/(\s+)/).filter((w) => w.length > 0);
          // Drop the textarea back to its pre-AI text so the typewriter
          // effect can build up from there. The interval (above) will
          // setValue one word at a time, the user sees the AI text
          // appear inside the textarea itself — no separate hint block.
          setValue(remarksField.id, prefix, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });
          setAiRemarksStream({
            fieldId: remarksField.id,
            prefix,
            words,
            revealedWords: 0,
          });
        }, 900);
        cascadeTimersRef.current.push(remarksTimer);
      }
    }

    // Mark the cascade as complete a hair after the last scheduled timer
    // so action buttons re-enable cleanly. Tag stagger ends at
    // `(N-1) * 280ms`; Remarks lands at 900ms. The Remarks timer is
    // always the latest of the two for typical N <= ~4.
    const cascadeEndsAt = Math.max(
      stagedAiTagIds.length > 0
        ? (stagedAiTagIds.length - 1) * 280
        : 0,
      remarksField && voiceResult.fields?.[remarksField.id] != null
        ? 900
        : 0,
    );
    const cascadeDoneTimer = setTimeout(
      () => setIsCascadeRunning(false),
      cascadeEndsAt + 200,
    );
    cascadeTimersRef.current.push(cascadeDoneTimer);

    voiceReset();
    // Do NOT return a cleanup that cancels timers here. voiceReset() nulls
    // voiceResult synchronously, which re-triggers this effect; React
    // fires the previous cleanup before re-running, which would clear the
    // timers we just scheduled. Cancellation on modal-close / record-swap
    // is handled by clearVoiceFillState() in the two reset effects above;
    // a brand-new voiceResult (rare — user fires voice again before the
    // current cascade ends) calls cancelCascade() at the top of this
    // effect via the new clearVoiceFillState path below.
  }, [
    voiceResult,
    voiceReset,
    cancelCascade,
    setValue,
    getValues,
    metadataFields,
    localTags,
  ]);

  // Fade out the ring highlight on non-remarks metadata fields ~2s after
  // they're applied. Remarks no longer participates in this ring set —
  // its visual signal is the "Added by voice" block below the field.
  useEffect(() => {
    if (highlightedFieldIds.size === 0) return;
    const timer = setTimeout(() => setHighlightedFieldIds(new Set()), 2000);
    return () => clearTimeout(timer);
  }, [highlightedFieldIds]);

  const handleAcceptAiTag = useCallback((id: string) => {
    setAiSelectedTagIds((prev) => prev.filter((x) => x !== id));
  }, []);

  // The "Use previous tags" switch. Additive on, subtractive-by-exactly-what-
  // it-added off: flipping it off must never take away a tag the user tapped
  // themselves, even if that tag also happens to be in the remembered set.
  const handlePreviousTagsToggle = useCallback(
    (next: boolean) => {
      const current =
        (getValues("__tags") as string[] | null | undefined) ?? [];
      if (next) {
        const applied = previousTagsMemory.tagIds.filter(
          (id) => !current.includes(id),
        );
        appliedPreviousTagsRef.current = applied;
        if (applied.length > 0) {
          setValue("__tags", [...current, ...applied], { shouldDirty: true });
        }
      } else {
        const applied = new Set(appliedPreviousTagsRef.current);
        appliedPreviousTagsRef.current = [];
        if (applied.size > 0) {
          setValue(
            "__tags",
            current.filter((id) => !applied.has(id)),
            { shouldDirty: true },
          );
        }
      }
      previousTagsMemory.setEnabled(next);
    },
    [getValues, setValue, previousTagsMemory],
  );

  // If the user focuses Remarks mid-stream, flush the typewriter to the
  // full text immediately so they don't see a half-written value in the
  // textarea they're about to edit.
  const handleRemarksFocus = useCallback(() => {
    setAiRemarksStream((prev) => {
      if (!prev) return prev;
      const full = prev.prefix + prev.words.join("");
      setValue(prev.fieldId, full, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      return null;
    });
  }, [setValue]);

  useEffect(() => {
    if (!isOpen) return;
    setIsEditMode(!isExistingRecord);
    setIsCloseConfirmOpen(false);
    setIsDeleteDialogOpen(false);
    setPhotoPendingRemoval(null);
    setIsExpandedPlan(false);
    setViewerIndex(null);
    setMultiPhotoError(null);
    setIsSharing(false);
    setShareError(null);
    setSubmitError(null);
    if (shareErrorTimeoutRef.current) {
      clearTimeout(shareErrorTimeoutRef.current);
      shareErrorTimeoutRef.current = null;
    }
    // Cancel any in-flight pre-warm for the previous record.
    shareAbortRef.current?.abort();
    shareAbortRef.current = null;
    sharePromiseRef.current = null;
    blobRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialRecord?.id]);

  const handleThumbnailPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (viewOnly || !isEditMode || !e.isPrimary) return;
      thumbnailPointerDownRef.current = { x: e.clientX, y: e.clientY };
    },
    [viewOnly, isEditMode],
  );

  const handleThumbnailPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (viewOnly || !isEditMode || !e.isPrimary || !planThumbnailRef.current) {
        return;
      }
      const down = thumbnailPointerDownRef.current;
      thumbnailPointerDownRef.current = null;
      if (!down) return;
      // A drag (not a tap) → leave it; never place a pin from a rotate sweep.
      if (
        Math.abs(e.clientX - down.x) >= THUMBNAIL_TAP_THRESHOLD_PX ||
        Math.abs(e.clientY - down.y) >= THUMBNAIL_TAP_THRESHOLD_PX
      ) {
        return;
      }
      const coords = clientToImageCoords(
        e.clientX,
        e.clientY,
        planThumbnailRef.current,
      );
      if (coords) {
        setValue("__pinX", coords.x, { shouldDirty: true });
        setValue("__pinY", coords.y, { shouldDirty: true });
      }
    },
    [viewOnly, isEditMode, setValue],
  );

  const handleThumbnailPointerCancel = useCallback(() => {
    thumbnailPointerDownRef.current = null;
  }, []);

  // Shared gate for record navigation: never while editing, never while an
  // expanded image/plan overlay is up (it owns the gestures + keys), and never
  // in view-only mode. Drives swipe and keyboard alike.
  const navEnabled =
    !isEditMode &&
    viewerIndex === null &&
    !isExpandedPlan &&
    !viewOnly;

  const swipeHandlers = useSwipeNavigation({
    onSwipeLeft: onNavigateNext,
    onSwipeRight: onNavigatePrev,
    enabled: navEnabled,
  });

  // Arrow-key navigation (US-004). Kept here rather than in the caller's
  // useModalNavigation hook so it respects the modal-only navEnabled gate and
  // so every call site gets it. Read the handlers through refs so the listener
  // subscribes once per open rather than re-binding whenever the caller hands
  // us fresh callback identities (onNext closes over a new fetchNextPage each
  // parent render). Ignore presses while a form field is focused so typing in
  // the metadata inputs doesn't flip records.
  const onNavigatePrevRef = useRef(onNavigatePrev);
  const onNavigateNextRef = useRef(onNavigateNext);
  onNavigatePrevRef.current = onNavigatePrev;
  onNavigateNextRef.current = onNavigateNext;
  useEffect(() => {
    if (!isOpen || !navEnabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        el?.getAttribute("contenteditable") === "true"
      ) {
        return;
      }
      if (e.key === "ArrowLeft") onNavigatePrevRef.current?.();
      else onNavigateNextRef.current?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, navEnabled]);

  const handleCloseAttempt = useCallback(() => {
    if (viewOnly || !isEditMode) {
      onClose();
      return;
    }
    if (!isExistingRecord || formState.isDirty) {
      setIsCloseConfirmOpen(true);
      return;
    }
    onClose();
  }, [
    viewOnly,
    isEditMode,
    isExistingRecord,
    formState.isDirty,
    onClose,
  ]);

  const handleConfirmClose = useCallback(() => {
    setIsCloseConfirmOpen(false);
    if (!isExistingRecord) {
      toast("Photo discarded");
    }
    onClose();
  }, [onClose, isExistingRecord]);

  const selectedPlan = projectPlans.find((p) => p.id === selectedPlanId) ?? null;
  const selectedPlanThumbnailUrl =
    selectedPlan?.latest_revision?.thumbnail_url ??
    selectedPlan?.latest_revision?.image_url ??
    null;

  const prefetchShareBlob = useCallback((): Promise<Blob | null> => {
    if (blobRef.current) return Promise.resolve(blobRef.current);
    if (sharePromiseRef.current) return sharePromiseRef.current;
    if (!imageSrc) return Promise.resolve(null);

    const filename = imageSrc.split("/").pop()?.split("?")[0] || "photo.jpg";
    const controller = new AbortController();
    shareAbortRef.current = controller;

    // Presigned S3 URLs authenticate via the X-Amz-Signature query param, not
    // cookies. Sending credentials is useless to S3 and breaks CORS, because a
    // credentialed request forbids the bucket's wildcard
    // Access-Control-Allow-Origin. See specs/technical/s3-image-fetching.spec.md.
    const promise = fetch(imageSrc, {
      credentials: "omit",
      signal: controller.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Pre-fetch failed: ${r.status}`);
        return r.blob();
      })
      .then((blob) => compressImage(blob, filename))
      .then((compressed) => {
        if (controller.signal.aborted) return null;
        blobRef.current = compressed;
        return compressed;
      })
      .catch((err) => {
        if (controller.signal.aborted) return null;
        console.error("Share pre-warm failed:", err);
        return null;
      })
      .finally(() => {
        if (sharePromiseRef.current === promise) {
          sharePromiseRef.current = null;
        }
      });

    sharePromiseRef.current = promise;
    return promise;
  }, [imageSrc]);

  // Pre-warm the share blob when the modal is open in view mode so it is
  // ready by the time the user taps Share. Runs at most once per record.
  useEffect(() => {
    if (!isOpen || isEditMode || !imageSrc) return;
    if (blobRef.current || sharePromiseRef.current) return;
    void prefetchShareBlob();
  }, [isOpen, isEditMode, imageSrc, prefetchShareBlob]);

  // Clean up the share-error auto-clear timer on unmount.
  useEffect(() => {
    return () => {
      if (shareErrorTimeoutRef.current) {
        clearTimeout(shareErrorTimeoutRef.current);
        shareErrorTimeoutRef.current = null;
      }
    };
  }, []);

  const handleShare = useCallback(async () => {
    if (!imageSrc || !canShare || isSharing) return;
    setIsSharing(true);
    // Clear any stale error from a prior attempt.
    setShareError(null);
    if (shareErrorTimeoutRef.current) {
      clearTimeout(shareErrorTimeoutRef.current);
      shareErrorTimeoutRef.current = null;
    }

    const remarksField = metadataFields.find(
      (f) => f.name.toLowerCase() === "remarks",
    );
    const remarksValue = remarksField
      ? String(initialRecord?.metadata?.[remarksField.id] ?? "").trim()
      : "";
    const textParts: string[] = [];
    if (selectedPlan?.name) textParts.push(`Plan: ${selectedPlan.name}`);
    if (remarksValue) textParts.push(remarksValue);
    const shareText = textParts.join("\n");

    const baseData: ShareData = { title: "Photo" };
    if (shareText) baseData.text = shareText;

    // Resolve the blob: use the cached ref if ready, otherwise race the
    // in-flight pre-warm against a 3.5s timeout so the navigator.share call
    // always happens within the browser's transient-activation window
    // (~5s across Chrome/Safari/Firefox). If the timeout wins, we fall back
    // to sharing `url: imageSrc`. The pre-warm itself is NOT cancelled so a
    // subsequent Share tap can still use the result.
    const BLOB_TIMEOUT_MS = 3500;
    let blob: Blob | null = blobRef.current;
    if (!blob) {
      const inflight = sharePromiseRef.current ?? prefetchShareBlob();
      blob = await Promise.race<Blob | null>([
        inflight,
        new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), BLOB_TIMEOUT_MS),
        ),
      ]);
    }

    let shareData: ShareData;
    if (blob) {
      const extension = blob.type.split("/")[1] || "jpg";
      const filename =
        imageSrc.split("/").pop()?.split("?")[0] || `photo.${extension}`;
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        shareData = { ...baseData, files: [file] };
      } else {
        shareData = { ...baseData, url: imageSrc };
      }
    } else {
      // Pre-warm failed, was aborted, or timed out — fall back to the image
      // URL, never window.location.href.
      shareData = { ...baseData, url: imageSrc };
    }

    try {
      await navigator.share(shareData);
    } catch (err) {
      // User cancellation (AbortError) is silent — no error UI, just return
      // the Share button to idle. Any other rejection (activation expired,
      // permission denied, quota, etc.) surfaces a visible inline error.
      if (err instanceof Error && err.name === "AbortError") {
        // Cancelled — no-op.
      } else {
        console.error("Share failed:", err);
        setShareError("Couldn't share. Try again.");
        if (shareErrorTimeoutRef.current) {
          clearTimeout(shareErrorTimeoutRef.current);
        }
        shareErrorTimeoutRef.current = setTimeout(() => {
          setShareError(null);
          shareErrorTimeoutRef.current = null;
        }, 4000);
      }
    } finally {
      setIsSharing(false);
    }
  }, [
    imageSrc,
    canShare,
    isSharing,
    prefetchShareBlob,
    metadataFields,
    initialRecord?.metadata,
    selectedPlan?.name,
  ]);

  const recordCreatorId = initialRecord?.created_by?.id ?? "";
  // Queued records are first-class editable records — same permission gates
  // as uploaded records. `viewOnly` remains the read-only flag for the
  // share-page use case; we check it independently of queued state.
  const canEdit =
    !viewOnly && isExistingRecord && initialRecord
      ? canOnResource("record", "Update", recordCreatorId)
      : false;
  const canSubmit =
    !viewOnly && isEditMode && (initialRecord
      ? canOnResource("record", "Update", recordCreatorId)
      : canCreate);
  const canDelete =
    !viewOnly && initialRecord
      ? canOnResource("record", "Delete", recordCreatorId)
      : false;
  // Gates the Autodesk push affordance in the view-mode sticky bar (Task 9):
  // rendered only when the project has a resolved, *active* connection —
  // `is_active` is a separate toggle from "a connection row exists", letting
  // a project pause pushes without deleting its Forma pairing. The hook
  // itself resolves a 404 (no connection configured) to `data: null`, so
  // `connection?.is_active` is already `undefined`/falsy in that case too.
  const { data: autodeskConnection } = useAutodeskConnection(
    projectId,
    !viewOnly,
  );
  const hasActiveAutodeskConnection = !!autodeskConnection?.is_active;
  // Mirrors [[record-share-menu.spec]]'s own render preconditions so the
  // sticky bar's gate can know, in advance, whether the Share trigger is
  // actually going to mount — without this, a viewer with an active
  // connection but no share/edit/delete rights would never see the bar at
  // all, even though the Autodesk destination alone is reason enough.
  // (A push-row 403 can still hide the Autodesk row after mount; that is
  // the menu's own call, and it degrades to a Share-only trigger.)
  const canPushToAutodesk =
    !viewOnly && isExistingRecord && !!initialRecord && hasActiveAutodeskConnection;

  const {
    submitRecord,
    submitMultiRecord,
    updateRecord,
    isSubmitting,
  } = useSubmitRecord();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  // Per-photo delete for a SYNCED multi-photo record (US-014, online-only):
  // optimistic removal from the records-list cache, idempotent 404. The hook
  // owns no UI — `handleRemovePhoto` passes the callbacks.
  const deleteRecordImage = useDeleteRecordImage();
  // Per-photo markup PATCH for a SYNCED record (US-003c, online-only): the
  // v2.0 per-photo endpoint; splices the normalized response over the cached
  // record. The hook owns no UI — `handleReEditComplete` passes the callbacks.
  const updateRecordImage = useUpdateRecordImage();
  const isBlobSrc = imageSrc?.startsWith("blob:") ?? false;

  // Re-edit flow state — used when the user taps a photo tile's "Edit markups"
  // pencil on an existing record (synced OR queued draft). The modal resolves
  // the tapped photo (its v2.0 `images[]` element, or its outbox `photos[]`
  // entry), loads THAT photo's original bytes, and mounts the markup editor
  // preloaded with its drawings + crop. On Done the modal saves through the
  // per-photo path `reEditTarget` names: the v2.0 per-photo PATCH for a synced
  // record, or a local `updatePendingRecordPhoto` write for a queued draft.
  const [reEditFile, setReEditFile] = useState<File | null>(null);
  // The pristine source bytes (EXIF intact, may still be HEIC) behind
  // `reEditFile`. The editor renders — and emits back byte-for-byte — the
  // browser-renderable copy from `ensureRenderable`, so we keep the raw
  // original here and write THAT back on save; otherwise re-editing a HEIC
  // draft would replace its untouched original (and `taken_at` EXIF) with the
  // transcoded JPEG. Mirrors the create flow's `originalFile` vs `renderFile`.
  const [reEditRawOriginal, setReEditRawOriginal] = useState<File | null>(null);
  const [reEditDrawings, setReEditDrawings] = useState<Drawing[] | undefined>(
    undefined,
  );
  const [reEditCrop, setReEditCrop] = useState<CropRect | null>(null);
  // The rotation the record was submitted with. The re-edit source is the
  // UNROTATED original, and the seeded drawings/crop are stored in the
  // rotation-corrected space — so without this the markups would land against
  // the wrong pixel grid. Absent on pre-rotation records, which means 0.
  const [reEditRotation, setReEditRotation] = useState<Rotation | undefined>(
    undefined,
  );
  const [isLoadingReEdit, setIsLoadingReEdit] = useState(false);
  const [reEditError, setReEditError] = useState<string | null>(null);
  // Which photo the OPEN re-edit session is editing — set when the editor
  // mounts, consumed by `handleReEditComplete` to route the save. `previewKey`
  // is the photo's TILE id (the key `photoTiles` / `viewerPhotos` carry), so
  // success can override exactly that tile's pixels.
  const [reEditTarget, setReEditTarget] = useState<
    | { kind: "synced"; imageId: string; previewKey: string }
    | { kind: "pending"; photoId: string | null; previewKey: string }
    | null
  >(null);
  // The variables of the markup PATCH currently in flight (or the one that just
  // failed), retained so a failure can be retried without the user redrawing
  // anything. The editor is gone by the time an error lands, so the payload has
  // to outlive it. Cleared on success and on a successful retry.
  const [pendingMarkupSave, setPendingMarkupSave] =
    useState<UpdateRecordImageVars | null>(null);

  // Post-re-edit preview URLs, keyed by TILE id — records-view owns
  // `selectedRecord` as a snapshot (not reactive to the query cache), and a
  // queued draft's per-photo object URLs (`usePendingRecordPhotos`) are never
  // re-minted for an edited photoId. Holding the freshly-rendered annotated
  // bytes locally updates exactly the edited photo's tile immediately. An
  // overridden tile also DROPS its offline-cache key — `OfflineImage` prefers
  // a cached blob over `src`, so a stale cached thumbnail would win otherwise
  // (the 2026-06-18 viewer lesson). Reset on record navigation; every URL
  // revoked on cleanup.
  const [reEditPreviews, setReEditPreviews] = useState<
    ReadonlyMap<string, string>
  >(new Map());

  const handleOpenReEditForPhoto = useCallback(
    async (tileId: string, tileIndex: number) => {
      if (!initialRecord) return;
      setReEditError(null);
      setIsLoadingReEdit(true);
      try {
        // A pending record is a local outbox draft — the server has never
        // heard of it, so re-edit reads the tapped photo's bytes + annotations
        // straight from the outbox entry. Fully offline, no server call.
        // An unreadable outbox answers "not a draft", so a synced photo
        // still re-edits from the server — see `findPendingRecord`.
        const pending = await findPendingRecord(initialRecord.id);
        if (pending) {
          // Resolve the tapped photo inside the entry. A resolved multi-draft
          // tile carries the photo's own `client_photo_uuid`; a single-photo
          // draft (or a legacy entry with no `photos[]`) has only its cover,
          // whatever synthetic id the tile carries — route that to the cover
          // (`photoId: null` in the save).
          const photos = pending.photos ?? [];
          const targetPhoto = photos.find((p) => p.photoId === tileId) ?? null;
          if (!targetPhoto && tileIndex !== 0) {
            // A non-cover tile that is not in `photos[]` — the draft changed
            // under us (removed photo / just-synced). Bail with the load error.
            throw new Error("This photo is no longer on the draft.");
          }
          const source = targetPhoto ?? photos[0] ?? null;
          // Already on the server (partial-sync window) — refuse at OPEN so no
          // markup work can be lost on save. The drain's mid-upload reconcile
          // replays only the COVER's markup bytes, so an edit of an
          // already-uploaded photo would silently vanish when the batch lands.
          if (source?.uploadStatus === "synced") {
            setReEditError("Already uploaded — reconnect to edit once synced");
            return;
          }
          const srcBlob = source?.originalBlob ?? pending.originalBlob;
          const srcName =
            (source ? source.originalFileName : pending.originalFileName) ||
            "original";
          const annotations = source?.annotations ?? pending.annotations;
          const rawPending = new File([srcBlob], srcName, {
            type: srcBlob.type || "application/octet-stream",
          });
          const file = await ensureRenderable(rawPending);
          setReEditDrawings(annotations.drawings);
          setReEditCrop(annotations.crop ?? null);
          setReEditRotation(toRotation(annotations.rotation));
          setReEditRawOriginal(rawPending);
          setReEditTarget({
            kind: "pending",
            photoId: targetPhoto?.photoId ?? null,
            previewKey: tileId,
          });
          setReEditFile(file);
          return;
        }
        // Synced record: the records list is v2.0, so the snapshot's
        // `images[]` element already carries `original_url` / `annotated_url` /
        // `annotations` (normalization is additive). An older cached snapshot
        // may lack the per-photo fields — re-fetch the v2.0 detail once
        // before giving up.
        const findTile = (record: ProjectRecord) =>
          recordImages(record).find((img) => img.id === tileId) ?? null;
        let img = findTile(initialRecord);
        if (!img || (img.original_url === undefined && img.annotated_url === undefined)) {
          const detail = await apiFetch<ProjectRecord>(
            `/projects/2.0/projects/${projectId}/records/${initialRecord.id}/`,
            getAccessToken,
          );
          img = findTile(normalizeRecordImageShape(detail));
        }
        if (!img) {
          throw new Error("This photo is no longer on the record.");
        }
        // Legacy rows have no `original_url` — fall back to the flattened
        // `annotated_url`. The editor treats those pixels as a regular
        // background; markups already baked in become un-undoable, and the
        // user's new vector strokes become the only undoable layer.
        const sourceUrl = img.original_url ?? img.annotated_url ?? null;
        if (!sourceUrl) {
          throw new Error("This record has no image on file.");
        }
        const res = await fetch(sourceUrl, { credentials: "omit" });
        if (!res.ok) throw new Error(`Failed to fetch image (${res.status})`);
        const blob = await res.blob();
        const rawFetched = new File([blob], "original", {
          type: blob.type || "application/octet-stream",
        });
        const file = await ensureRenderable(rawFetched);
        const drawings = (img.annotations?.drawings ?? []) as Drawing[];
        const crop = (img.annotations?.crop ?? null) as CropRect | null;
        setReEditDrawings(drawings);
        setReEditCrop(crop);
        setReEditRotation(toRotation(img.annotations?.rotation));
        setReEditRawOriginal(rawFetched);
        setReEditTarget({ kind: "synced", imageId: img.id, previewKey: img.id });
        setReEditFile(file);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Couldn't load original image";
        console.error("Re-edit load failed:", error);
        setReEditError(message);
      } finally {
        setIsLoadingReEdit(false);
      }
    },
    [initialRecord, projectId, getAccessToken],
  );

  const handleReEditClose = useCallback(() => {
    setReEditFile(null);
    setReEditRawOriginal(null);
    setReEditDrawings(undefined);
    setReEditCrop(null);
    setReEditRotation(undefined);
    setReEditTarget(null);
    setReEditError(null);
  }, []);

  const handleReEditComplete = useCallback(
    async (payload: MarkupCompletePayload) => {
      if (!initialRecord || !reEditTarget) return;
      // Persist the pristine source (raw HEIC/EXIF preserved), not the
      // editor's renderable copy. Falls back to the editor's passthrough
      // when no raw original was captured.
      const rawOriginal = reEditRawOriginal ?? payload.originalFile;
      // Local preview URL for exactly the edited photo's tile — records-view
      // owns `selectedRecord` as a snapshot, so the freshly-rendered bytes are
      // swapped in locally. Cache reconciliation / invalidation refreshes the
      // grid; this override keeps the OPEN modal in sync until the next
      // render. The same tile's previous URL is revoked on rapid consecutive
      // re-edits.
      //
      // Sourced from the THUMBNAIL, never `annotatedBlob`: a grid tile is
      // ~120px, and pointing it at a multi-megapixel JPEG costs a full decode
      // to paint plus resident bytes for every edited tile on screen.
      const mintPreview = () => {
        const url = URL.createObjectURL(payload.thumbnailBlob);
        const key = reEditTarget.previewKey;
        setReEditPreviews((prev) => {
          const next = new Map(prev);
          const stale = next.get(key);
          if (stale) URL.revokeObjectURL(stale);
          next.set(key, url);
          return next;
        });
      };
      // Queued draft — a local IDB write against the tapped photo's `photos[]`
      // entry; cover edits re-mirror the top-level fields inside the helper.
      // No server call (the draft was never uploaded).
      if (reEditTarget.kind === "pending") {
        try {
          const result = await updatePendingRecordPhoto(
            initialRecord.id,
            reEditTarget.photoId,
            {
              annotatedBlob: payload.annotatedBlob,
              originalBlob: rawOriginal,
              originalFileName: rawOriginal.name || null,
              annotations: {
                drawings: payload.drawings,
                crop: payload.crop,
                rotation: payload.rotation,
                downscale_max_px: payload.downscaleMaxPx,
              },
            },
          );
          // Close first in EVERY outcome. The error line lives in the modal
          // body, underneath the portaled editor — reporting a refusal without
          // closing renders it where the user cannot see it, which is
          // indistinguishable from the save silently doing nothing.
          if (result === "synced") {
            // The photo uploaded while the editor was open (partial-sync race).
            handleReEditClose();
            setReEditError("Already uploaded — reconnect to edit once synced");
            return;
          }
          if (result === "not-found") {
            handleReEditClose();
            setReEditError("This draft is no longer available.");
            return;
          }
          void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
          mintPreview();
          handleReEditClose();
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : "Failed to update draft markups";
          console.error("Update pending record markups failed:", error);
          // A failed markup write to a PENDING (outbox) record is silent data
          // loss — the user's re-edited annotations on a local draft were
          // dropped. Report it (additive to the inline error) — see
          // [[error-reporting]].
          Sentry.captureException(error, {
            level: "error",
            tags: { area: "outbox", phase: "idb-write" },
            contexts: {
              outbox: { clientUuid: initialRecord.id, projectId },
            },
          });
          handleReEditClose();
          setReEditError(message);
        }
        return;
      }
      // Synced record — the v2.0 per-photo PATCH. Full annotations shape,
      // `rotation` included (the retired record-level path dropped it, which
      // mis-seeded the next re-edit of a rotated photo).
      const vars = {
        projectId,
        recordId: initialRecord.id,
        imageId: reEditTarget.imageId,
        annotatedBlob: payload.annotatedBlob,
        thumbnailBlob: payload.thumbnailBlob,
        originalFile: rawOriginal,
        annotations: {
          drawings: payload.drawings,
          crop: payload.crop,
          rotation: payload.rotation,
          downscaleMaxPx: payload.downscaleMaxPx,
        },
      };
      // Show the result and get out of the way BEFORE the upload. The annotated
      // pixels already exist client-side, so nothing visual has any reason to
      // wait on a multipart PATCH that carries the full-resolution original —
      // holding the user on an unchanged fullscreen editor for its duration is
      // what made a working save read as a dead button.
      mintPreview();
      handleReEditClose();
      // Retained so a failure can be retried without redrawing anything.
      setPendingMarkupSave(vars);
      updateRecordImage.mutate(vars, {
        onSuccess: () => setPendingMarkupSave(null),
        onError: (error) =>
          setReEditError(error.message || "Failed to update markups"),
      });
    },
    [
      initialRecord,
      reEditTarget,
      reEditRawOriginal,
      updateRecordImage,
      projectId,
      queryClient,
      handleReEditClose,
    ],
  );

  // Re-send the markup PATCH that failed, using the payload we held on to. The
  // markup work itself is never lost to a failed save — the user does not have
  // to reopen the editor and redraw.
  const handleRetryMarkupSave = useCallback(() => {
    if (!pendingMarkupSave) return;
    setReEditError(null);
    updateRecordImage.mutate(pendingMarkupSave, {
      onSuccess: () => setPendingMarkupSave(null),
      onError: (error) =>
        setReEditError(error.message || "Failed to update markups"),
    });
  }, [pendingMarkupSave, updateRecordImage]);

  // Reset + revoke the local preview URL when the modal navigates to a
  // different record (the fresh record comes with its own canonical image).
  useEffect(() => {
    return () => {
      setReEditPreviews((prev) => {
        for (const url of prev.values()) URL.revokeObjectURL(url);
        return new Map();
      });
      // Both are scoped to the record we are leaving. The modal is a carousel
      // ("Record 4 of 82"), so leaving a stale failure behind would show the
      // previous record's error against this one — and its Retry would PATCH
      // an image the user is no longer looking at. The failure is still
      // reported to Sentry from the mutation's own onError.
      setPendingMarkupSave(null);
      setReEditError(null);
    };
  }, [initialRecord?.id]);

  // The latest local re-edit of the COVER photo, if any — the cover slot in
  // `effectiveSrc`. The cover's TILE id differs per mode: a resolved queued
  // multi draft keys its tiles by outbox photoId; everything else keys by the
  // record's cover image id (a pending placeholder's cover id is the
  // clientUuid).
  const coverPreviewUrl =
    (isMultiPhotoDraft && pendingDraftPhotos[0]
      ? reEditPreviews.get(pendingDraftPhotos[0].photoId)
      : undefined) ??
    (initialRecord
      ? reEditPreviews.get(coverImage(initialRecord)?.id ?? initialRecord.id)
      : undefined) ??
    null;
  // Offline: prefer locally-rendered previews. `coverPreviewUrl` is always a
  // local blob (from the markup editor) so it's safe. `imageSrc` is safe only
  // when it's a `blob:` URL — that's a fresh client-side capture before the
  // upload commits, which we want to keep visible even if the user toggles
  // airplane mode after shooting. The rotating S3 URL fallbacks are dropped;
  // a synced record falls back to its offline-cached thumbnail blob
  // (`cachedThumbUrl`), or `null` when that record is not in the cache.
  //
  // A pending record resolves to the live `pendingTileUrl` (via `highResSrc`),
  // online or offline — `usePendingRecords` re-mints that URL whenever an edit
  // bumps the draft's revision, so it is never stale. `coverPreviewUrl` still
  // wins when set: a re-edit just produced fresh annotated bytes locally and
  // we show them until the pending query catches up. The frozen-snapshot
  // `imageSrc` is never used for a pending record.
  const effectiveSrc = isPendingRecord
    ? (coverPreviewUrl ?? pendingTileUrl)
    : isOnlineStatus
      ? (coverPreviewUrl ?? highResSrc ?? imageSrc)
      : (coverPreviewUrl ?? (isBlobSrc ? imageSrc : cachedThumbUrl));

  // Live ref for `isPendingRecord` so the `onBatchReady` callback (fired
  // after the camera session closes) reads the CURRENT state rather than the
  // closure captured at hook-instantiation time. Needed because the record may
  // sync while the camera is open — the ref reads the state as it is when the
  // batch actually lands.
  const isPendingRecordRef = useRef(isPendingRecord);
  useEffect(() => {
    isPendingRecordRef.current = isPendingRecord;
  });

  // "+ Add" on a PENDING+OFFLINE record appends photos to the queued CREATE
  // entry's `photos[]` so they upload together via the v2.0 multi-drain.
  // Invalidating `["pendingRecords"]` causes `usePendingRecordPhotos` to
  // re-read the entry and surface the new photos in the open viewer.
  const handleAppendToPendingDraft = useCallback(
    async (batch: CapturedPhoto[]) => {
      if (!isExistingRecord || !initialRecord) return;
      await appendPhotosToPendingRecord(
        initialRecord.id,
        batch.map(capturedToPendingPhoto),
      );
      await queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
    },
    [isExistingRecord, initialRecord, queryClient],
  );

  // "+ Add" on an EXISTING, synced record hands the whole captured batch to
  // the durable [[append-photo-outbox]]: one entry per photo, then a single
  // `["pendingAppends"]` invalidation so the optimistic tiles light up. This is
  // ADD-ONLY — the record already owns its plan/pin/tags/metadata, and the
  // server ignores them on append, so no form state is read here. Fire-and-
  // forget: the IDB writes settle asynchronously off the camera's sync Done
  // callback (see the `onBatchReady` wiring below) so the capture surface
  // closes immediately with no spinner. Declared above the `if (!imageSrc)
  // return` guard because Hooks must run unconditionally on every render.
  const handleAppendBatch = useCallback(
    async (batch: CapturedPhoto[]) => {
      // Existing, synced record only — `handleAddPhoto` already guards this,
      // but re-checked so a draft can never reach the append outbox.
      if (!isExistingRecord || !initialRecord) return;
      const recordId = initialRecord.id;
      const now = Date.now();
      await Promise.all(
        batch.map((photo) =>
          putPendingAppend({
            photoId: photo.id,
            recordId,
            projectId,
            annotatedBlob: photo.annotatedBlob,
            originalBlob: photo.originalFile,
            annotations: {
              drawings: photo.drawings,
              crop: photo.crop,
              downscaleMaxPx: photo.downscaleMaxPx,
            },
            takenAtHint: photo.takenAtHint,
            status: "pending",
            createdAt: now,
          }),
        ),
      );
      // Refresh the optimistic tiles in the open viewer (and the grid badge).
      // The drain invalidates this same prefix — keep it identical.
      await queryClient.invalidateQueries({ queryKey: ["pendingAppends"] });
    },
    [isExistingRecord, initialRecord, projectId, queryClient],
  );

  // The "+ Add" camera reuses the create flow's full-batch capture core
  // ([[record-capture.spec]]) — single/multi toggle, filmstrip, per-photo
  // markup — with its terminal swapped from "open a new-record modal" to the
  // append handoff above. Same option set the create FAB callers pass
  // (bottom-nav / plan-viewer). `onBatchReady` is a SYNC callback; fire the
  // async outbox writes via a void promise so Done never blocks.
  const addPhotoCapture = useRecordCapture({
    projectId,
    canOnResource,
    canCreate,
    canCreateTag,
    metadataFields,
    projectTags: localTags,
    projectPlans,
    onBatchReady: (batch) => {
      // Read the ref at fire time — the record's state is current AFTER the
      // camera session, not at hook-instantiation. Edge case: if the record
      // syncs DURING the camera session, `isPendingRecordRef.current` flips
      // false and the batch routes to `handleAppendBatch` with the temp id —
      // a recoverable failed tile. Acceptable for v1; not special-cased.
      void (isPendingRecordRef.current
        ? handleAppendToPendingDraft(batch)
        : handleAppendBatch(batch));
    },
  });

  // The side panel sits in the page beside the gallery rather than over it,
  // so it takes the phone's single-column layout; the full-screen dialog does
  // too. See [[image-viewer-modal.spec]] § Presentations.
  const isPanel = presentation === "panel";
  const singleColumn = presentation !== "dialog";

  // A dialog gets Escape from Radix; the panel is not a dialog, so it listens
  // itself. Anything layered above it (the photo carousel, the plan view, a
  // confirm, a sheet) owns Escape while it is up.
  const hasOverlayOpen =
    viewerIndex !== null ||
    isExpandedPlan ||
    isDeleteDialogOpen ||
    isRaiseIssueOpen ||
    isCloseConfirmOpen ||
    photoPendingRemoval !== null ||
    reEditFile !== null;
  useEffect(() => {
    if (!isPanel || !isOpen || hasOverlayOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Portaled surfaces this component doesn't track (tag create, the
      // capture flow, a dropdown) are dialogs/menus in the DOM — leave them be.
      if (
        document.querySelector(
          '[role="dialog"], [role="alertdialog"], [role="menu"]',
        )
      ) {
        return;
      }
      handleCloseAttempt();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isPanel, isOpen, hasOverlayOpen, handleCloseAttempt]);

  // Lets a host that can swap the record from outside (the Photos page's
  // gallery beside the panel) ask before discarding an edit in progress.
  const hasUnsavedChanges =
    isOpen && isExistingRecord && isEditMode && !viewOnly && formState.isDirty;
  const onUnsavedChangesChangeRef = useRef(onUnsavedChangesChange);
  onUnsavedChangesChangeRef.current = onUnsavedChangesChange;
  useEffect(() => {
    onUnsavedChangesChangeRef.current?.(hasUnsavedChanges);
  }, [hasUnsavedChanges]);

  if (!imageSrc) return null;

  const submitForm = async (data: Record<string, unknown>) => {
    // Authoritative gate: never fire a mutation while edit/delete is locked
    // (syncing draft, draft-synced-while-open, or synced-offline). The disabled
    // Update button is the primary guard; this makes the rule hold even if a
    // submit is triggered another way (e.g. Enter), so a stale `clientUuid` is
    // never PATCHed. A new-record create is never blocked (not an existing
    // record), so this does not affect the create path.
    if (recordActionsBlocked) return;
    const planId = (data.__plan as string | null | undefined) ?? null;
    const pinX = (data.__pinX as number | null | undefined) ?? null;
    const pinY = (data.__pinY as number | null | undefined) ?? null;
    const pinBearing = (data.__pinBearing as number | null | undefined) ?? null;
    const tags = (data.__tags as string[] | undefined) ?? [];

    setSubmitError(null);
    if (isExistingRecord && initialRecord) {
      // Synced-record update: server PATCH with a spinner; success flips back
      // to view mode and toasts, failure keeps the modal open for retry.
      await updateRecord({
        projectId,
        recordId: initialRecord.id,
        planId,
        pinX,
        pinY,
        pinBearing,
        metadataFields,
        tags,
        data,
        onSuccess: () => {
          resetForm(data);
          setIsEditMode(false);
          toast("Record updated", {
            icon: <Check className="h-3.5 w-3.5" />,
          });
        },
        onError: (error) => {
          setSubmitError(error);
          console.error("Submission error:", error);
        },
      });
      return;
    }

    // New record. A MULTI-photo batch (>1 captured photo) queues to the durable
    // local outbox as ONE record carrying every photo — instant, exactly like
    // the single-photo path below (queue-and-close, no network, no spinner).
    // Background sync (`useOutboxDrain`) uploads each photo to the v2.0 endpoint
    // under the record's shared `client_record_uuid`. See [[offline-record-outbox]].
    if (hasCapturedBatch && capturedPhotos!.length > 1) {
      await submitMultiRecord({
        projectId,
        capturedPhotos: capturedPhotos!,
        planId,
        pinX,
        pinY,
        pinBearing,
        metadataFields,
        tags,
        issue: buildPendingIssue(data),
        data,
        onSuccess: () => {
          // Rolling memory: the record just created defines "previous" for the
          // next capture. Creates only — an edit is not a new record.
          previousTagsMemory.remember(tags);
          resetForm(data);
          onClose();
        },
        onError: (error) => {
          setSubmitError(error);
          console.error("Submission error:", error);
        },
      });
      return;
    }

    // Single-photo new record — queue to the durable local outbox, then close.
    // The IDB write is local and fast (no network), so awaiting it keeps the
    // UX instant while guaranteeing the capture is durably saved BEFORE the
    // modal closes. If the write fails (storage quota, IDB unavailable) the
    // modal stays open and surfaces the error so the worker can retry —
    // closing on a failed write would silently lose the photo. Background sync
    // uploads the queued record — see [[offline-record-outbox]] L1.
    const singlePhoto = hasCapturedBatch ? capturedPhotos![0] : null;
    const originalFile = singlePhoto?.originalFile ?? imageFile;
    const blob = singlePhoto?.annotatedBlob ?? annotatedBlob;
    if (!originalFile || !blob) return;
    await submitRecord({
      originalFile,
      annotatedBlob: blob,
      projectId,
      planId,
      pinX,
      pinY,
      pinBearing,
      metadataFields,
      tags,
      annotations: singlePhoto
        ? { drawings: singlePhoto.drawings, crop: singlePhoto.crop }
        : { drawings, crop },
      takenAtHint: singlePhoto?.takenAtHint ?? takenAtHint,
      issue: buildPendingIssue(data),
      data,
      onSuccess: () => {
        // Rolling memory — see the multi-photo path above.
        previousTagsMemory.remember(tags);
        resetForm(data);
        onClose();
      },
      onError: (error) => {
        setSubmitError(error);
        console.error("Submission error:", error);
      },
    });
  };

  // The form's submit handler. Every expected failure above reports itself
  // through `onError`; this catches the rest — a throw the save path did not
  // anticipate — so a tap on Save never ends in silence. Without it a rejected
  // promise escaped `handleSubmit` as an unhandled rejection: no request, no
  // message, the button looking like it did nothing. See
  // [[image-viewer-modal.spec]] § States.
  const onSubmit = async (data: Record<string, unknown>) => {
    try {
      await submitForm(data);
    } catch (error) {
      console.error("Submission error:", error);
      Sentry.captureException(error, {
        level: "error",
        tags: { area: "record-viewer", phase: "submit" },
      });
      setSubmitError(
        isExistingRecord
          ? "Couldn't save those changes. Try again."
          : "Couldn't save this record. Try again.",
      );
    }
  };

  // Name when the creator has one, email when they don't. Names are optional
  // on the backend user record and blank for accounts that didn't arrive with
  // an SSO profile, which is what used to render "Uploaded by unknown".
  // `created_by` is expanded onto the record payload we already hold, so the
  // email costs no extra request. Null = no identity at all (share view,
  // un-synced draft) — the line is dropped rather than left dangling.
  const uploaderName = personDisplayName(initialRecord?.created_by);

  // When the record's LATEST photo was taken, not when the row was written: an
  // uploaded file carries its EXIF timestamp and an in-app capture its capture
  // moment, both resolved server-side onto `taken_at`. Falls back to the
  // record's own created date when no photo carries one. See
  // [[record-photo-grid]].
  const latestPhotoLabel = initialRecord
    ? formatCapturedAt(recordLatestPhotoAt(initialRecord))
    : null;

  // ---- Photo grid + fullscreen carousel data ----------------------------
  // The set of photos shown in the 3-col grid and fullscreen carousel. One
  // photo or many — built uniformly via the [[record-images]] seam for an
  // existing record, or from the captured batch for a new record.
  //
  // Existing-record cover (index 0) keeps today's offline-aware resolution:
  // its `src` is exactly `effectiveSrc` (cached thumbnail blob offline,
  // high-res online, re-edit preview when set) — NEVER a raw server URL, so an
  // uncached record offline shows nothing (via OfflineImage's empty-src
  // degradation) rather than a dead S3 URL. Its key is `thumbAssetKey(record)`.
  // Additional photos (i > 0) resolve their per-image cached blob via
  // OfflineImage; their live server URL is supplied only while online for the
  // same no-dead-URL-offline reason.
  //
  // Optimistic append tiles whose synced twin is already on the record — matched
  // by `client_photo_uuid`, which the v2.0 read echoes on every photo — are
  // dropped here: the server photo now stands in for them. This is what makes the
  // append handoff seamless. An optimistic tile shows only while no server photo
  // carries its uuid, so when its upload lands (the record's `images[]` gains the
  // photo) the tile is replaced in place — never doubled, and never lost when the
  // outbox entry is retired moments later. See [[append-photo-outbox]] § Success.
  const syncedAppendUuids = new Set(
    (isExistingRecord && initialRecord ? recordImages(initialRecord) : [])
      .map((img) => img.client_photo_uuid)
      .filter((uuid): uuid is string => !!uuid),
  );
  const visiblePendingAppendTiles = pendingAppendTiles.filter(
    (t) => !syncedAppendUuids.has(t.photoId),
  );
  const basePhotoTiles: RecordPhotoTile[] = (() => {
    // A queued MULTI-photo draft renders its whole group from the outbox
    // entry's per-photo blobs (`pendingDraftPhotos`), NOT from `recordImages`
    // — the synthesized placeholder record carries only the cover image. The
    // cover (i=0) still flows through `effectiveSrc` (the live `pendingTileUrl`,
    // re-minted on edit); the rest use their freshly minted blob URLs with an
    // empty asset key (local bytes, no cache, online or offline). No append
    // tiles: a draft is not yet a server record, so it has no append outbox.
    if (isExistingRecord && initialRecord && isMultiPhotoDraft) {
      return pendingDraftPhotos.map((photo, i) => ({
        id: photo.photoId,
        assetKey: "",
        src: i === 0 ? (effectiveSrc ?? photo.url) : photo.url,
      }));
    }
    // First frame of a queued multi-photo draft: `pendingDraftPhotos` has not
    // resolved yet (so `isMultiPhotoDraft` is still false), but we already KNOW
    // it's a multi draft. Render ONLY the cover this frame — the generic branch
    // below would otherwise map the placeholder's N images, whose non-cover
    // entries reuse the cover URL, flashing N identical covers. See code-review S1.
    if (isExistingRecord && initialRecord && isPendingMultiDraftEntry) {
      return [
        {
          id: initialRecord.id,
          assetKey: "",
          src: effectiveSrc ?? pendingTileUrl ?? "",
        },
      ];
    }
    if (isExistingRecord && initialRecord) {
      const images = recordImages(initialRecord);
      const serverTiles: RecordPhotoTile[] = images.map((img, i) => {
        if (i === 0) {
          return {
            id: img.id,
            assetKey: thumbAssetKey(initialRecord),
            src: effectiveSrc ?? "",
          };
        }
        return {
          id: img.id,
          assetKey: thumbAssetKeyFor(img.id, initialRecord.updated_at),
          src: isOnlineStatus ? (img.thumbnail ?? img.image ?? "") : "",
        };
      });
      // Optimistic append tiles render as ORDINARY tiles AFTER the server
      // images, in capture order, from local bytes — no offline-cache key, no
      // syncing badge ([[append-photo-outbox]]). A `failed` tile carries the
      // tap-to-retry affordance (handled by `handleRetryAppend`).
      const appendTiles: RecordPhotoTile[] = visiblePendingAppendTiles.map(
        (t) => ({
          id: t.photoId,
          assetKey: "",
          src: t.previewUrl,
          pendingAppend: t.status,
        }),
      );
      return [...serverTiles, ...appendTiles];
    }
    // New record — captured batch (local blob previews, no offline cache key).
    if (hasCapturedBatch) {
      return capturedPhotos!.map((p: CapturedPhoto) => ({
        id: p.id,
        assetKey: "",
        src: p.previewUrl,
      }));
    }
    // Legacy single capture — the one preview blob.
    return [{ id: "capture-0", assetKey: "", src: effectiveSrc ?? imageSrc ?? "" }];
  })();

  // Per-photo re-edit preview overrides: the edited photo's tile renders the
  // freshly-rendered local bytes, and DROPS its offline-cache key —
  // `OfflineImage` prefers a cached blob over `src`, so a stale cached
  // thumbnail would otherwise win over the fresh preview (the 2026-06-18
  // viewer lesson).
  const applyReEditPreviews = (tiles: RecordPhotoTile[]): RecordPhotoTile[] =>
    reEditPreviews.size === 0
      ? tiles
      : tiles.map((tile) => {
          const override = reEditPreviews.get(tile.id);
          return override ? { ...tile, assetKey: "", src: override } : tile;
        });
  const photoTiles = applyReEditPreviews(basePhotoTiles);

  // Full-resolution sources for the fullscreen carousel. UNLIKE the grid tiles,
  // the fullscreen viewer must show the FULL-RES image, not the thumbnail. The
  // offline asset cache only ever holds the thumbnail (asset-keys.ts caches
  // `img.thumbnail`), and `OfflineImage` prefers a cached blob over its `src` —
  // so keying a slide off the thumb cache pins the viewer to the thumbnail the
  // moment auto-cache-on-open populates it, even online (the bug this fixes).
  // Fix: ONLINE pass an EMPTY asset key, which disables the cache lookup so the
  // full-res `src` renders (the cover's high-res `effectiveSrc`; each additional
  // photo's `image` URL). OFFLINE fall back to the thumb key → cached thumbnail,
  // matching US-001's offline rule. (`main`'s single-image viewer already loaded
  // full-res this way via [[expanded-image-view.spec]]'s progressive image; the
  // multi-photo redesign regressed it.) The grid (`photoTiles`) is unchanged.
  const baseViewerPhotos: RecordPhotoTile[] =
    // A queued MULTI-photo draft has only its local annotated bytes per photo
    // (no separate full-res), so every slide is the minted blob URL — empty
    // asset key, online or offline. The cover delegates to `effectiveSrc` so a
    // re-edit preview is honored, mirroring the synced-record cover rule.
    isExistingRecord && initialRecord && isMultiPhotoDraft
      ? pendingDraftPhotos.map((photo, i) => ({
          id: photo.photoId,
          assetKey: "",
          src: i === 0 ? (effectiveSrc ?? photo.url) : photo.url,
        }))
      : // First frame of a queued multi-photo draft (blobs not resolved yet) —
      // show only the cover, never N placeholder cover-copies. See code-review S1.
      isExistingRecord && initialRecord && isPendingMultiDraftEntry
      ? [
          {
            id: initialRecord.id,
            assetKey: "",
            src: effectiveSrc ?? pendingTileUrl ?? "",
          },
        ]
      : isExistingRecord && initialRecord
      ? [
          ...recordImages(initialRecord).map((img, i) =>
            i === 0
              ? {
                  id: img.id,
                  // Cover: delegate entirely to `effectiveSrc` (full-res online,
                  // cached-thumb / re-edit blob offline) — never key off the
                  // thumb cache, so a re-edit preview is never overridden.
                  assetKey: "",
                  src: effectiveSrc ?? "",
                }
              : {
                  id: img.id,
                  assetKey: isOnlineStatus
                    ? ""
                    : thumbAssetKeyFor(img.id, initialRecord.updated_at),
                  src: isOnlineStatus ? (img.image ?? img.thumbnail ?? "") : "",
                },
          ),
          // Mirror the grid: optimistic tiles trail the server photos so a tap
          // on a non-failed pending tile (its grid index ≥ server count) opens
          // the matching carousel slide. From local bytes — same offline-safe
          // empty key as the grid. Deduped against synced twins, exactly like
          // the grid, so the carousel indices line up with the visible tiles.
          ...visiblePendingAppendTiles.map((t) => ({
            id: t.photoId,
            assetKey: "",
            src: t.previewUrl,
          })),
        ]
      : photoTiles;
  // Each slide carries its grid tile's thumbnail and offline key for the
  // viewer's thumbnail strip, so the strip shows the same bytes the grid does
  // (cached offline, re-edit preview included) and never pulls full-size photos
  // for 56px tiles. Matched by id: the tile and slide lists share ids and order.
  // A slide with no tile (a pending draft's first frame) falls back to its own
  // image. See [[record-photo-viewer]] § US-PV-004.
  const tileById = new Map(photoTiles.map((tile) => [tile.id, tile]));
  const viewerPhotos: RecordPhotoViewerItem[] = applyReEditPreviews(
    baseViewerPhotos,
  ).map((photo) => {
    const tile = tileById.get(photo.id);
    return tile
      ? { ...photo, thumbnailSrc: tile.src, thumbnailAssetKey: tile.assetKey }
      : photo;
  });

  // Grid "+ Add" routing: an existing record opens the FULL-batch in-app camera
  // (the hook — instantiated above the `if (!imageSrc) return` guard — drives
  // camera → markup → append-outbox handoff, with the single/multi toggle and
  // the gallery button part of that camera UI); a new, not-yet-saved draft is
  // owned by the create pipeline ([[record-capture.spec]]), so adding here
  // surfaces the not-yet state rather than engaging the camera (see Out of
  // Scope). Plain event handler (not a hook), so it can live after the early
  // guard.
  const handleAddPhoto = () => {
    // A brand-new in-progress draft (no initialRecord) is owned by the create
    // pipeline ([[record-capture.spec]]): re-enter the camera to append more
    // photos to the same working batch. The modal stays mounted through the
    // detour, so the form state survives. Supplied only by the create pipeline.
    if (!isExistingRecord || !initialRecord) {
      onAddPhotos?.();
      return;
    }
    // Pending + online: the draft is syncing; its real id isn't settled yet so
    // we can't safely append. Lock until the upload lands.
    if (isPendingRecord && isOnlineStatus) {
      setMultiPhotoError("Syncing… available to edit once saved");
      return;
    }
    // Pending + offline: append to the queued CREATE entry so the new photos
    // upload together with the rest of the draft via the v2.0 multi-drain.
    if (isPendingRecord && !isOnlineStatus) {
      addPhotoCapture.openCamera();
      return;
    }
    // Synced while this modal was open (openedAsDraft latched): the modal still
    // holds the temp clientUuid, so a server append would 404. Reopen the synced
    // record to add more photos.
    if (openedAsDraft) {
      setMultiPhotoError("Saved — reopen to add photos");
      return;
    }
    // Synced record: open the camera, append to the server via the append outbox.
    addPhotoCapture.openCamera();
  };

  // Tap-to-retry on a FAILED optimistic tile: return that append to `pending`
  // (clearing its stored error) and refresh the queue so background sync fires
  // it again ([[append-photo-outbox]]). Fire-and-forget — the tile flips back
  // to an ordinary pending tile on the next `["pendingAppends"]` settle.
  const handleRetryAppend = (index: number) => {
    if (!initialRecord) return;
    // Optimistic tiles trail the server photos in the grid, so the append's
    // position is `index - serverCount`. Index into the SAME deduped list the
    // grid renders (`visiblePendingAppendTiles`) so a synced-and-hidden tile
    // can't shift the mapping — a failed tile never has a synced twin, so it is
    // never deduped, but staying on the visible list keeps the indices exact.
    const serverCount = recordImages(initialRecord).length;
    const tile = visiblePendingAppendTiles[index - serverCount];
    if (!tile) return;
    void (async () => {
      await updatePendingAppendStatus(tile.photoId, "pending");
      await queryClient.invalidateQueries({ queryKey: ["pendingAppends"] });
    })();
  };

  // Removing a photo. A record must keep >= 1 photo: removing the only photo
  // is a record delete (confirm dialog). Removing one of many targets the
  // v2.0 per-photo delete (not live yet → graceful message).
  const handleRemovePhoto = (index: number) => {
    // A brand-new capture's batch is owned by the create pipeline. The lone
    // photo's "×" discards (closing the flow); a >1 batch removes that photo
    // from the working batch by the TILE's own id (so removing the cover targets
    // the right photo). The capture hook re-points the cover preview when needed.
    if (!isExistingRecord || !initialRecord) {
      if (photoTiles.length <= 1) {
        // Only photo of a brand-new capture → discarding it is closing.
        handleCloseAttempt();
        return;
      }
      const tile = photoTiles[index];
      if (!tile) return;
      onRemovePhoto?.(tile.id);
      return;
    }
    // Existing record. Deleting the lone photo == deleting the record. The true
    // photo count of a queued MULTI-photo draft is its outbox `photos[]` length
    // (`pendingDraftPhotos`), NOT `recordPhotoCount` — the synthesized
    // placeholder carries only the cover image, which would otherwise make every
    // remove on a draft read as a record delete.
    const photoCount = isMultiPhotoDraft
      ? pendingDraftPhotos.length
      : recordPhotoCount(initialRecord);
    if (photoCount <= 1) {
      setIsDeleteDialogOpen(true);
      return;
    }
    // One of many. Resolve the tapped photo by the TILE's own id (never a
    // re-derived index into another array) so deleting the cover can't target
    // the wrong photo.
    const tile = photoTiles[index];
    if (!tile) return;
    // Queued offline draft: the photo lives only in the outbox entry's
    // photos[]. Splice it locally — no server call — and refresh the draft's
    // photo query so the tile disappears.
    if (isMultiPhotoDraft) {
      const recordId = initialRecord.id;
      setMultiPhotoError(null);
      void (async () => {
        const result = await removePhotoFromPendingRecord(recordId, tile.id);
        // The photo already uploaded during a partial sync (it lives on the
        // server under this draft's record id). It cannot be dropped locally
        // without leaving a ghost, and a per-photo server delete needs
        // connectivity — so tell the user to reconnect once the draft syncs.
        if (result === "synced") {
          setMultiPhotoError("Already uploaded — reconnect to remove once synced");
          return;
        }
        await queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
      })();
      return;
    }
    // Synced record (>1 photo). Per-photo delete is online-only: the "×" only
    // renders in edit mode and a synced record can only enter edit mode online,
    // so this is effectively unreachable offline — still guard defensively with
    // the same copy the synced Edit/Delete affordances use when offline.
    if (!isOnlineStatus) {
      setMultiPhotoError("Offline — reconnect to edit");
      return;
    }
    const recordId = initialRecord.id;
    // Removing one photo of a synced record is permanent and project-wide, so
    // it is confirmed before it fires — a stray tap on the small per-tile "×"
    // must not silently destroy a photo. `tile.id` is the server image id (from
    // `recordImages` above); hold it for the confirm and let `confirmRemovePhoto`
    // run the actual mutation. The optimistic patch + idempotent-404 live in the
    // hook and only run once confirmed.
    setMultiPhotoError(null);
    setPhotoPendingRemoval({ recordId, imageId: tile.id });
  };

  // A tile's "Edit markups" pencil (US-003b/c). Mirrors `handleRemovePhoto`'s
  // routing: a NEW batch delegates to the create pipeline (which re-enters the
  // markup editor at that index); an existing record — queued draft or synced —
  // opens the per-photo re-edit for the TILE's own photo id (never a
  // re-derived index), so editing the cover can't target the wrong photo.
  const handleEditPhotoMarkups = (index: number) => {
    if (!isExistingRecord || !initialRecord) {
      // Mirrors the retired button's voice gate: taps are ignored while the
      // AI voice fill is mid-flight.
      if (isVoiceBusy) return;
      onEditMarkups?.(index);
      return;
    }
    const tile = photoTiles[index];
    if (!tile) return;
    // Defensive offline guard for a SYNCED record (edit mode is normally
    // unreachable offline for synced records — same guard as per-photo
    // delete). A queued draft edits fully offline.
    if (!isPendingRecord && !isOnlineStatus) {
      setReEditError("Offline — reconnect to edit");
      return;
    }
    void handleOpenReEditForPhoto(tile.id, index);
  };

  // Confirmed synced per-photo delete (from [[remove-photo-dialog]]). Runs the
  // optimistic mutation against the held photo id; closes the confirm on settle.
  const confirmRemovePhoto = () => {
    if (!photoPendingRemoval) return;
    const { recordId, imageId } = photoPendingRemoval;
    setMultiPhotoError(null);
    deleteRecordImage.mutate(
      { projectId, recordId, imageId },
      {
        onSuccess: () => {
          setPhotoPendingRemoval(null);
        },
        onError: (err) => {
          // Close the confirm so the next step is visible (inline error, or the
          // record-delete dialog for a 409).
          setPhotoPendingRemoval(null);
          // 409 = "a record must keep its last photo". It shouldn't occur (the
          // lone-photo "×" routes to record-delete before reaching here), but
          // surface it defensively: route to the record-delete dialog so the
          // user has a real next step rather than a dead-end error.
          if (err instanceof ApiError && err.status === 409) {
            setIsDeleteDialogOpen(true);
            return;
          }
          setMultiPhotoError("Couldn't delete photo. Try again.");
        },
      },
    );
  };

  // Record-to-record edge arrows are shown only in view mode (never edit/new).
  const showEdgeArrows = mode === "view";
  // "Record N of M" counter — shown in view/edit, hidden in new mode.
  const showRecordCounter =
    mode !== "new" &&
    totalCount != null &&
    totalCount > 0 &&
    currentIndex != null &&
    currentIndex >= 0;

  const recordTitle = initialRecord ? "Edit Record" : "New Record";

  const body = (
      <>

        {/* The "+ Add" tile no longer clicks a bare file input — it opens the
            full-batch in-app camera ([[record-capture.spec]]) for existing
            records. The camera (with its single/multi toggle + gallery button)
            owns the library <input> now; see the `useRecordCapture` instance
            and `{addPhotoCapture.captureUI}` below. */}

        {/* ===== Top app bar: Close + "Record N of M" counter ===== */}
        <div
          className="relative z-[25] flex shrink-0 items-center gap-2 border-b border-border/70 bg-background/90 px-2.5 py-2.5 backdrop-blur"
          style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 0.625rem)" }}
        >
          <button
            type="button"
            onClick={handleCloseAttempt}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          {showRecordCounter && (
            <span className="text-[13px] font-medium text-muted-foreground">
              Record <b className="font-semibold text-foreground">{currentIndex! + 1}</b> of{" "}
              {totalCount}
            </span>
          )}
          <div className="flex-1" />
          {/* In the side panel the edge arrows would sit over the seam with the
              gallery, so record-to-record navigation moves into the top bar. */}
          {isPanel && showEdgeArrows && (onNavigatePrev || onNavigateNext) && (
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={onNavigatePrev}
                disabled={!onNavigatePrev}
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:opacity-40"
                aria-label="Previous record"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={onNavigateNext}
                disabled={!onNavigateNext}
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:opacity-40"
                aria-label="Next record"
              >
                {isFetchingNextPage ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* ===== Record-to-record edge arrows (view mode only) ===== */}
        {!isPanel && showEdgeArrows && onNavigatePrev && (
          <button
            type="button"
            onClick={onNavigatePrev}
            className="absolute left-0 top-[58%] z-[36] flex h-14 w-10 -translate-y-1/2 items-center justify-center rounded-r-lg bg-black/40 text-white transition-colors hover:bg-black/60"
            aria-label="Previous record"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {!isPanel && showEdgeArrows && onNavigateNext && (
          <button
            type="button"
            onClick={onNavigateNext}
            className="absolute right-0 top-[58%] z-[36] flex h-14 w-10 -translate-y-1/2 items-center justify-center rounded-l-lg bg-black/40 text-white transition-colors hover:bg-black/60"
            aria-label="Next record"
          >
            {isFetchingNextPage ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <ChevronRight className="h-6 w-6" />
            )}
          </button>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn(
            "flex min-h-0 w-full flex-1 flex-col",
            !singleColumn && "md:h-full md:min-h-0",
          )}
          {...swipeHandlers}
        >
          <div
            className={cn(
              "flex min-h-0 w-full flex-1 flex-col overflow-y-auto",
              !singleColumn && "md:flex-row md:overflow-hidden",
            )}
          >
            {/* Photo grid side */}
            <div
              className={cn(
                "w-full shrink-0 bg-background p-2.5",
                !singleColumn &&
                  "md:h-full md:w-[55%] md:overflow-y-auto md:border-r md:border-border",
              )}
            >
              <RecordPhotoGrid
                photos={photoTiles}
                editable={mode !== "view" && !viewOnly}
                canAdd={!viewOnly}
                onOpen={(i) => setViewerIndex(i)}
                onAdd={handleAddPhoto}
                onRemove={handleRemovePhoto}
                onEditMarkups={
                  // Preserve the retired button's gates exactly: existing
                  // records need edit mode + record:Update + an unblocked
                  // record; new records need the create pipeline's handler
                  // (the voice gate lives in handleEditPhotoMarkups).
                  isExistingRecord
                    ? isEditMode && canEdit && !recordActionsBlocked
                      ? handleEditPhotoMarkups
                      : undefined
                    : onEditMarkups
                      ? handleEditPhotoMarkups
                      : undefined
                }
                onRetry={handleRetryAppend}
                // Uploader meta rides the count row rather than owning a line
                // in the details column. New mode has no uploader yet, and a
                // record with no creator identity shows none.
                uploadedBy={
                  mode !== "new" && isExistingRecord
                    ? (uploaderName ?? undefined)
                    : undefined
                }
                latestPhotoAt={
                  mode !== "new" && isExistingRecord
                    ? (latestPhotoLabel ?? undefined)
                    : undefined
                }
              />
              {/* The Edit-markups entry point is the per-tile pencil in the
                  grid above (US-003b/c) — the old single below-the-grid button
                  is retired. This line surfaces the re-edit load state. */}
              {isLoadingReEdit && (
                <p
                  role="status"
                  aria-live="polite"
                  className="inline-flex items-center gap-1.5 px-1 pt-2 text-xs text-muted-foreground"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading photo…
                </p>
              )}
              {/* The markup PATCH runs after the editor has closed, so this is
                  the only thing telling the user an upload is still going. It
                  is deliberately non-blocking — the tile already shows the
                  edit, and the rest of the record stays interactive. */}
              {updateRecordImage.isPending && (
                <p
                  role="status"
                  aria-live="polite"
                  className="inline-flex items-center gap-1.5 px-1 pt-2 text-xs text-muted-foreground"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving markups…
                </p>
              )}
              {(reEditError || multiPhotoError) && (
                <p
                  role="status"
                  aria-live="polite"
                  className="px-1 pt-2 text-xs text-destructive"
                >
                  {reEditError ?? multiPhotoError}
                  {reEditError && pendingMarkupSave && (
                    <button
                      type="button"
                      onClick={handleRetryMarkupSave}
                      disabled={updateRecordImage.isPending}
                      className="ml-2 font-medium underline underline-offset-2 disabled:opacity-50"
                    >
                      Retry
                    </button>
                  )}
                </p>
              )}
            </div>

            {/* Metadata side */}
            <div
              data-slot="record-details-column"
              className={cn(
                "w-full shrink-0 space-y-4 bg-background p-4",
                !singleColumn && "md:h-full md:w-[45%] md:overflow-y-auto",
              )}
            >
              {/* Issues raised against this record, at the top of the details
                  column — above the uploader line and the Plan section, the
                  same order the narrow layout has always shown. It is a child
                  of this column rather than a sibling of it: as a sibling it
                  became a third flex column on desktop, which is not what
                  [[record-issues-band.spec]] describes. The negative margins
                  bleed it back out to the column's edges so it still reads as
                  a band rather than an inset card. Mounted only when the
                  caller opted in — it fetches, and the share page must never
                  reach that endpoint. */}
              {showRecordIssues && isExistingRecord && initialRecord && (
                <RecordIssuesBand
                  projectId={projectId}
                  recordId={initialRecord.id}
                  highlightIssueId={justRaisedIssueId}
                  className="-mx-4 -mt-4"
                />
              )}

              <div className="space-y-2">
                <Label>Plan</Label>
                {!isEditMode && selectedPlan && (
                  <p className="text-sm text-muted-foreground">
                    {selectedPlan.name}
                    {selectedPlan.deleted_at != null ? " (Archived)" : ""}
                  </p>
                )}
                {!isEditMode && !selectedPlan && (
                  <p className="text-sm text-muted-foreground">No plans yet</p>
                )}
                {isEditMode && !viewOnly && availablePlans.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Select plan &amp; tap on plan to pin location.
                  </p>
                )}
                {isEditMode && availablePlans.length === 0 && (
                  <p className="text-sm text-muted-foreground">No plans yet</p>
                )}
                {isEditMode && (isExistingRecord || availablePlans.length > 0) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        type="button"
                        disabled={viewOnly}
                      >
                        {selectedPlan ? (
                          selectedPlan.deleted_at != null ? (
                            <span className="text-muted-foreground">
                              {selectedPlan.name} (Archived)
                            </span>
                          ) : (
                            selectedPlan.name
                          )
                        ) : (
                          "Select Plan..."
                        )}
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                      <DropdownMenuItem
                        onClick={() =>
                          setValue("__plan", null, { shouldDirty: true })
                        }
                      >
                        None
                      </DropdownMenuItem>
                      {projectPlans
                        .filter((plan) => plan.deleted_at == null)
                        .map((plan) => (
                          <DropdownMenuItem
                            key={plan.id}
                            onClick={() =>
                              setValue("__plan", plan.id, {
                                shouldDirty: true,
                              })
                            }
                          >
                            {plan.name}
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {selectedPlan && (
                  <>
                    <div
                      className={cn(
                        "relative w-full overflow-hidden rounded-md border bg-secondary/10 transition-opacity",
                        !viewOnly &&
                          isEditMode &&
                          "cursor-crosshair hover:opacity-95",
                      )}
                      onPointerDown={handleThumbnailPointerDown}
                      onPointerUp={handleThumbnailPointerUp}
                      onPointerCancel={handleThumbnailPointerCancel}
                    >
                      <button
                        type="button"
                        className="absolute bottom-2 right-2 z-10 rounded-md bg-black/50 p-1.5 text-white transition-colors hover:bg-black/70"
                        aria-label="Expand plan"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsExpandedPlan(true);
                        }}
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>
                      <OfflineImage
                        ref={planThumbnailRef}
                        assetKey={
                          selectedPlan.latest_revision
                            ? planAssetKey(
                                selectedPlan.id,
                                selectedPlan.latest_revision.id,
                                selectedPlan.latest_revision.tile_status,
                              )
                            : `plan:${selectedPlan.id}:none`
                        }
                        src={selectedPlanThumbnailUrl ?? ""}
                        alt={selectedPlan.name}
                        className="block h-auto w-full"
                        draggable={false}
                      />
                      {pinX !== null && pinY !== null && (
                        <>
                          {/* View cone in BOTH modes — the thumbnail shows a
                              single pin (this record's), so the crowding that
                              motivates the browse chevron doesn't apply. The
                              faint at-rest preview is edit-only (you can only
                              aim in edit mode); the rotate handle below is too.
                              See [[directional-pins]]. */}
                          <div
                            className="pointer-events-none absolute"
                            style={{
                              left: `${pinX * 100}%`,
                              top: `${pinY * 100}%`,
                            }}
                          >
                            <PinDirectionCone
                              bearing={
                                canSetThumbnailDirection
                                  ? pinBearing ?? THUMBNAIL_HANDLE_REST_BEARING
                                  : pinBearing
                              }
                              preview={canSetThumbnailDirection && pinBearing == null}
                            />
                          </div>
                          {/* Pin marker — the shared round dot (static; the
                              thumbnail has no zoom). In edit mode it is also the
                              rotate gesture's pin-centre anchor (box centre = pin
                              point). See [[pin-marker-dot.spec]]. */}
                          <PinMarkerDot
                            ref={thumbnailPinDotRef}
                            xPct={pinX}
                            yPct={pinY}
                            size={12}
                            scaleWithZoom={false}
                          />
                          {/* Rotate handle — edit mode only; drag to set/adjust
                              direction inline. Shared arrow-lever; the gesture
                              stops propagation so it never moves the pin. On this
                              overflow-hidden thumbnail an edge pin's handle is
                              clipped. See [[pin-rotate-handle.spec]]. */}
                          {canSetThumbnailDirection && (
                            <PinRotateHandle
                              xPct={pinX}
                              yPct={pinY}
                              bearing={pinBearing}
                              restBearing={THUMBNAIL_HANDLE_REST_BEARING}
                              radiusPx={THUMBNAIL_CONE_RADIUS_PX}
                              handleProps={thumbnailRotateProps}
                              ariaLabel="Drag to set the direction the photo was taken"
                            />
                          )}
                          {/* Transient finger coachmark — edit mode only, until
                              a direction is dragged in. Like the handle, an edge
                              pin's hint can be clipped by the overflow-hidden
                              thumbnail. See [[pin-direction-hint.spec]]. */}
                          {canSetThumbnailDirection && pinBearing == null && (
                            <PinDirectionHint
                              key={`${pinX},${pinY}`}
                              xPct={pinX}
                              yPct={pinY}
                              radiusPx={THUMBNAIL_CONE_RADIUS_PX}
                              restBearing={THUMBNAIL_HANDLE_REST_BEARING}
                            />
                          )}
                        </>
                      )}
                      {/* Direction hint banner — names the gesture the finger
                          coachmark mimes; shown while the thumbnail can set a
                          direction (edit mode + a pin). See [[directional-pins]]. */}
                      {canSetThumbnailDirection && pinX !== null && pinY !== null && (
                        <div className="pointer-events-none absolute inset-x-0 top-2 z-10 flex justify-center px-2">
                          <span className="rounded-full bg-black/65 px-3 py-1.5 text-center text-xs font-medium text-white shadow-lg">
                            {PIN_DIRECTION_HINT_TEXT}
                          </span>
                        </div>
                      )}
                      {!viewOnly && isEditMode && pinX === null && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <div className="flex items-center gap-2 rounded-full bg-black/65 px-4 py-2.5 text-sm font-medium text-white shadow-lg">
                            <MapPin className="h-4 w-4 animate-bounce" />
                            Tap to pin your location
                          </div>
                        </div>
                      )}
                    </div>
                    {!viewOnly && isEditMode && pinX !== null && pinY !== null && (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setValue("__pinX", null, { shouldDirty: true });
                            setValue("__pinY", null, { shouldDirty: true });
                            // A bearing requires a pin — clearing the pin clears
                            // the direction too. See [[directional-pins]].
                            setValue("__pinBearing", null, { shouldDirty: true });
                          }}
                          className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          Remove pin
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {isEditMode && !viewOnly && isOnlineStatus && voiceSupported && (
                <VoiceCaptureButton
                  state={voiceState}
                  error={voiceError}
                  isSupported={voiceSupported}
                  elapsedMs={voiceElapsedMs}
                  onStart={voiceStart}
                  onStop={voiceStop}
                  onDismissError={voiceReset}
                />
              )}

              <div className="rounded-md">
                <TagSelector
                  availableTags={
                    isEditMode
                      ? localTags
                      : localTags.filter((t) => selectedTags.includes(t.id))
                  }
                  selectedTags={selectedTags}
                  aiSelectedTagIds={aiSelectedTagIds}
                  aiPromotedOrder={aiPromotedOrder}
                  onAcceptAiTag={handleAcceptAiTag}
                  showAiChoosingHint={voiceState === "processing"}
                  onChange={(next) =>
                    setValue("__tags", next, { shouldDirty: true })
                  }
                  disabled={viewOnly || !isEditMode}
                  projectId={projectId}
                  onTagCreated={(tag) =>
                    setLocalTags((prev) => [...prev, tag])
                  }
                  canCreateTag={canCreateTag && isOnlineStatus}
                  previousTags={
                    // Every new record, empty set included. Gating on
                    // "has something to offer" made the switch appear and
                    // disappear on state the user cannot see; it now renders
                    // consistently and explains its own inert state.
                    mode === "new"
                      ? {
                          count: previousTagsMemory.tagIds.length,
                          enabled: previousTagsMemory.enabled,
                          onChange: handlePreviousTagsToggle,
                        }
                      : undefined
                  }
                />
              </div>

              {metadataFields.length > 0 ? (
                <div className="grid w-full gap-4">
                  {metadataFields.map((field) => {
                    const isRemarks =
                      field.name.toLowerCase() === "remarks";
                    const isHighlighted = highlightedFieldIds.has(field.id);
                    // While the inline Remarks typewriter is running for
                    // this field, mark the wrapper so E2E + screen
                    // readers can detect AI-sourced content.
                    const isStreamingHere =
                      isRemarks &&
                      aiRemarksStream?.fieldId === field.id;
                    return (
                      <div
                        key={field.id}
                        className={cn(
                          "rounded-md transition-shadow duration-300",
                          isHighlighted &&
                            "ring-2 ring-tertiary/60 ring-offset-2 ring-offset-background",
                        )}
                        onFocus={
                          isRemarks ? handleRemarksFocus : undefined
                        }
                        data-ai-source={
                          isStreamingHere ? "true" : undefined
                        }
                      >
                        {renderField(
                          field,
                          control,
                          viewOnly || !isEditMode,
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No metadata fields configured for this project.
                </p>
              )}

              {/* "Promote as issue" — raise one in the same act as the
                  capture. New records only, and only for someone who may raise
                  one. Sits below the metadata fields (Remarks included) because
                  the issue is raised off what was just written, not before it. */}
              {mode === "new" && canCreateIssue && !viewOnly && (
                <RaiseIssueToggle
                  projectId={projectId}
                  value={issueDraft}
                  onChange={setIssueDraft}
                  disabled={isSubmitting}
                />
              )}

              {/* Inline status region. The CTAs live in the sticky bottom
                  bars below; errors + the offline/sync lock message stay here
                  in the metadata flow so they sit next to the fields and are
                  found by screen readers regardless of which bar is shown. */}
              <div className="space-y-2">
                {submitError && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="text-center text-sm text-destructive"
                  >
                    {submitError}
                  </p>
                )}
                {canShare && !isEditMode && shareError && (
                  <p
                    role="status"
                    aria-live="polite"
                    className="text-center text-sm text-destructive"
                  >
                    {shareError}
                  </p>
                )}
                {/* Edit/Delete are locked in three states — a draft mid-sync, a
                    draft synced while open, and a synced record offline. The
                    affordances in the bars below are disabled; this explains
                    why. Never a raw error, never a lost edit. See
                    [[offline-record-outbox]]. */}
                {recordActionsBlocked && (canEdit || canDelete || canSubmit) && (
                  <p
                    role="status"
                    aria-live="polite"
                    className="text-center text-xs text-muted-foreground"
                  >
                    {recordActionsBlockedMessage}
                  </p>
                )}
                {viewOnly && imageSrc && (
                  <Button
                    type="button"
                    variant="default"
                    className="w-full gap-2"
                    asChild
                  >
                    <a
                      href={imageSrc}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="h-4 w-4" />
                      Download Image
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* ===== Sticky bottom bar (mode-specific) ===== */}
          {mode === "view" &&
            (canShare ||
              canEdit ||
              canDelete ||
              canPushToAutodesk ||
              canRaiseIssue) && (
            <div
              className="z-[25] flex shrink-0 items-stretch gap-2 border-t border-border bg-background/92 px-3.5 py-2.5 backdrop-blur"
              style={{
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.625rem)",
              }}
            >
              {(canShare || canPushToAutodesk) && initialRecord && (
                <RecordShareMenu
                  projectId={projectId}
                  recordId={initialRecord.id}
                  hasActiveConnection={canPushToAutodesk}
                  canShare={canShare}
                  isSharing={isSharing}
                  onShare={() => void handleShare()}
                  disabled={recordActionsBlocked}
                  disabledReason={recordActionsBlockedMessage ?? undefined}
                />
              )}
              {canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isSubmitting || recordActionsBlocked || isVoiceBusy}
                  className="h-[46px] basis-[50px] grow-0 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Delete Record"
                >
                  <Trash2 className="h-[17px] w-[17px]" />
                </Button>
              )}
              {canEdit && (
                <Button
                  type="button"
                  variant={canRaiseIssue ? "outline" : "default"}
                  onClick={() => setIsEditMode(true)}
                  disabled={recordActionsBlocked}
                  // Edit demotes to an icon when New issue is available, so
                  // the primary slot goes to the action that starts work rather
                  // than the one that corrects a field.
                  className={
                    canRaiseIssue
                      ? "h-[46px] basis-[50px] grow-0 p-0"
                      : "h-[46px] flex-1 gap-2"
                  }
                  aria-label={canRaiseIssue ? "Edit Record" : undefined}
                >
                  <Pencil className="h-[17px] w-[17px]" />
                  {!canRaiseIssue && "Edit"}
                </Button>
              )}
              {canRaiseIssue && (
                <Button
                  type="button"
                  onClick={() => setIsRaiseIssueOpen(true)}
                  disabled={recordActionsBlocked}
                  className="h-[46px] flex-1 gap-2"
                >
                  <ClipboardList className="h-[17px] w-[17px]" />
                  New issue
                </Button>
              )}
            </div>
          )}

          {mode === "edit" && (
            <div
              className="z-[25] flex shrink-0 gap-2.5 border-t border-border bg-background/92 px-4 py-3 backdrop-blur"
              style={{
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)",
              }}
            >
              <Button
                type="button"
                variant="outline"
                className="h-[46px] basis-[38%] grow-0"
                onClick={handleCloseAttempt}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {canSubmit && (
                <Button
                  type="submit"
                  className="h-[46px] flex-1"
                  loading={isSubmitting}
                  disabled={recordActionsBlocked || isVoiceBusy}
                >
                  Save changes
                </Button>
              )}
            </div>
          )}

          {mode === "new" && (
            <div
              className="z-[25] flex shrink-0 gap-2.5 border-t border-border bg-background/92 px-4 py-3 backdrop-blur"
              style={{
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)",
              }}
            >
              {canSubmit && (
                <Button
                  type="submit"
                  className="h-[46px] flex-1"
                  loading={isSubmitting}
                  disabled={isVoiceBusy}
                >
                  {issueDraft ? "Create & raise issue" : "Create record"}
                </Button>
              )}
            </div>
          )}
        </form>

        {/* Fullscreen photo carousel — opened from a grid tile. Reuses the
            ExpandedImageView zoom/pan engine inside each slide. See
            [[record-photo-viewer]]. */}
        {viewerIndex !== null && viewerPhotos.length > 0 && (
          <RecordPhotoViewer
            photos={viewerPhotos}
            initialIndex={viewerIndex}
            onClose={() => setViewerIndex(null)}
          />
        )}

        {isExpandedPlan && selectedPlan && (
          <ExpandedPlanView
            plan={selectedPlan}
            projectId={projectId}
            pinX={pinX}
            pinY={pinY}
            bearing={pinBearing}
            onClose={() => setIsExpandedPlan(false)}
            isEditMode={isEditMode && !viewOnly}
            onPinChange={(x, y) => {
              setValue("__pinX", x, { shouldDirty: true });
              setValue("__pinY", y, { shouldDirty: true });
            }}
            onBearingChange={(b) => {
              setValue("__pinBearing", b, { shouldDirty: true });
            }}
          />
        )}

        {initialRecord && (
          <DeleteRecordDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onDeleted={onClose}
            projectId={projectId}
            recordId={initialRecord.id}
          />
        )}

        {canRaiseIssue && initialRecord && (
          <RaiseIssueSheet
            projectId={projectId}
            recordId={initialRecord.id}
            open={isRaiseIssueOpen}
            onOpenChange={setIsRaiseIssueOpen}
            // The record's remarks are usually already a description of the
            // problem, so they seed the title rather than making the user
            // retype them. Still editable — the two are not always the same
            // sentence.
            defaultTitle={recordRemarks}
            // You stay on the photo. One photo often carries several defects,
            // and navigating away would make the second one a round trip.
            onRaised={(issue) => {
              toast("Issue raised", { icon: <Check className="h-4 w-4" /> });
              setJustRaisedIssueId(issue.id);
              window.setTimeout(() => setJustRaisedIssueId(null), 4000);
            }}
          />
        )}

        <RemovePhotoDialog
          isOpen={photoPendingRemoval !== null}
          onConfirm={confirmRemovePhoto}
          onCancel={() => setPhotoPendingRemoval(null)}
          isPending={deleteRecordImage.isPending}
        />

        {/* Per-photo re-edit editor — mounted only when the user has tapped a
            photo tile's Edit-markups pencil on an existing record AND that
            photo's original bytes have finished loading. Discard and Done both
            route back through handleReEditClose so the modal's metadata side
            is preserved. */}
        {reEditFile && (
          <ImageMarkupEditor
            isOpen={!!reEditFile}
            onClose={handleReEditClose}
            imageFile={reEditFile}
            onComplete={handleReEditComplete}
            initialDrawings={reEditDrawings}
            initialCrop={reEditCrop}
            initialRotation={reEditRotation}
          />
        )}

        {/* "+ Add" capture surface (existing records): the full-batch in-app
            camera ([[record-capture.spec]]) → per-photo markup. Its surfaces
            are isOpen-driven and render null when idle; on Done the batch is
            handed to `handleAppendBatch`, which queues each photo to the
            [[append-photo-outbox]]. The new-record modal is NEVER mounted in
            this terminal (the `onBatchReady` handoff suppresses it). */}
        {addPhotoCapture.captureUI}
      </>
  );

  const closeConfirm = (
      <AlertDialog
        open={isCloseConfirmOpen}
        onOpenChange={setIsCloseConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isExistingRecord ? "Unsaved changes" : "Discard photo?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isExistingRecord
                ? "Unsaved changes will be discarded. Are you sure?"
                : "This photo and any details you've entered will be lost."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Go back</AlertDialogCancel>
            <AlertDialogAction className="h-12" onClick={handleConfirmClose}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
  );

  if (isPanel) {
    return (
      <>
        {isOpen && (
          <section
            aria-label={recordTitle}
            data-slot="record-side-panel"
            data-modal-nav-disabled={
              isExpandedPlan || viewerIndex !== null || undefined
            }
            className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-background"
          >
            <h2 className="sr-only">{recordTitle}</h2>
            {body}
          </section>
        )}
        {closeConfirm}
      </>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseAttempt();
      }}
    >
      <DialogContent
        className={cn(
          "max-w-none max-h-none w-screen h-dvh p-0 border-none rounded-none translate-x-0 translate-y-0 top-0 left-0 [&>div]:p-0 flex flex-col bg-background overflow-y-auto",
          // The full-screen presentation keeps the phone layout at every width.
          // The dialog primitive caps width at sm; full-screen lifts that cap.
          singleColumn && "sm:max-w-none",
          !singleColumn &&
            "md:overflow-hidden md:max-w-[1100px] md:h-[90dvh] md:max-h-[90dvh] md:w-full md:rounded-lg md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
        )}
        data-modal-nav-disabled={
          isExpandedPlan || viewerIndex !== null || undefined
        }
        showCloseButton={false}
        onPointerDownOutside={(e) => {
          if (isEditMode && !viewOnly) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleCloseAttempt();
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{recordTitle}</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
      {closeConfirm}
    </Dialog>
  );
}
